import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { validateReframePreview } from "@/lib/reframe-geometry";
import { readImageEditStatus, waitForImageEditCheck } from "@/lib/image-edit-observation";
import { groupAnglesByFamily } from "@/lib/angle-groups";
import { filterSavedAnglesByStage, SAVED_ANGLE_STAGE_OPTIONS, type SavedAngleStage } from "@/lib/saved-angle-stage";
import {
  canGenerateAds,
  countHiddenAdsExpressTemplates,
  getAdsExpressBrowsePool,
  getHistoricalAdsExpressAngleDisplay,
} from "@/pages/ads-express-gating";
import { useParams, Link, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { VariantPicker } from "@/components/variant-picker";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Zap, Loader2, CheckCircle2, XCircle, Download, RotateCcw, Search, Check, Plus,
  Users, Package, Sparkles, Megaphone, ArrowLeft, ArrowRight, Image as ImageIcon, X, Maximize2, Building2,
  Wand2, Paintbrush, Bookmark, BookmarkCheck, Folder, Crop, Film, Percent,
  ImageOff, ImagePlus, CalendarClock, ArrowDownWideNarrow, ChevronDown, ChevronUp, BookOpen,
  Sliders, Type, MessageSquare, CopyPlus,
} from "lucide-react";
import { GOOGLE_FONT_CATALOG } from "@shared/google-fonts";
import { ensureGoogleFontsLoaded } from "@/lib/brand-font-loader";
import {
  isAdSteerableBrandFont,
  brandFontRoleFromLabel,
  BRAND_FONT_ROLE_OPTIONS,
} from "@shared/brand-fonts";
import type { BrandFont } from "@shared/schema";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { InpaintingModal } from "@/components/campaign-wizard/inpainting-modal";
import { AdImage } from "@/components/ad-image";
import { UndoAdEditButton, RedoAdEditButton, useUndoableAdIds, useRedoableAdIds, invalidateUndoableAds } from "@/components/undo-ad-edit";
import { InlineRenameTitle } from "@/components/inline-rename-title";
import { sanitizeFilename, buildZipFileNames } from "@/lib/filename";
import { AdsExpressPreviousSessions } from "@/components/ads-express-previous-sessions";
import {
  AdsExpressRunQueuePanel,
  useAdsExpressRunQueue,
  invalidateAdsExpressRunQueue,
} from "@/components/ads-express-run-queue";
import { AdsExpressRecentGenerations } from "@/components/ads-express-recent-generations";
import { TemplateCategoriesManager } from "@/components/template-categories-manager";
import { useAuth } from "@/hooks/use-auth";
import { useAdNotesEnabled, useAdNoteCounts, AdNotesDialog } from "@/components/ad-notes";
import type { Persona, PersonaCharacter, Product, NarrativeAngle, AdUnitCatalogItem, AdUnitType, Brand, Project, Offer, SalesDiscount, StaticTemplateSet, StaticTemplateSetMember } from "@shared/schema";
import { type AdImageRecord, displayUrl, normalizeExampleImage, hasUsableExampleImage } from "@shared/adImage";
import { apiRequest, getQueryFn, invalidateGeneratedAdListings, queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
import { type AspectRatio, AD_ASPECT_RATIO_OPTIONS, normalizeAspectRatio, safeZoneBaseRenderRatio } from "@shared/aspectRatios";
import { SafeZoneOverlay, SafeZoneToggle } from "@/components/safe-zone-overlay";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import { loadLastAspectRatios, saveLastAspectRatios } from "@/lib/aspectRatio";
import { PickerSelect } from "@/components/picker-select";
import { AdsExpressItemFilterHelp } from "@/components/ads-express-item-filter-help";
import { useAdsExpressItemFilter } from "@/hooks/use-ads-express-item-filter";
import { findPendingImageEdit, clearPendingImageEdit, imageEditPhaseLabel, updatePendingImageEdit, persistPendingImageEdit } from "@/lib/pending-image-edits";
import { usePendingImageEditsRevision } from "@/hooks/use-pending-image-edits";
import { duplicateAdAsIs, copyAndReframeAd } from "@/lib/ad-copy";
import { canOpenPromptImageEditor } from "@shared/prompt-image-edit";
import { AdsExpressStyleRulesDialog } from "@/components/ads-express-style-rules-control";
import { ADS_EXPRESS_IMAGE_ENGINE_SELECTOR_FLAG, type AdsExpressImageEngineChoice } from "@shared/ads-express-engine-selection";
import { adsExpressAttachmentFormState, buildAdGenerationPayload, narrativeAngleMatchesAdsExpressAttachment } from "@shared/ad-generation-request";
import { MAX_STANDARD_AD_REFERENCES as MAX_AD_REFERENCES, standardAdReferenceCapacityError as adReferenceCapacityError, planStandardAdImageBudget } from "@shared/ads-express-image-budget";
import { ADS_EXPRESS_REFERENCE_UPLOAD_PATH, ADS_EXPRESS_REFERENCE_ACCEPT, ADS_EXPRESS_REFERENCE_UPLOAD_GUIDANCE, adsExpressReferenceUploadError } from "@shared/ads-express-reference-upload";
import { resolveExtraStandardProductPhotos, resolvePrimaryStandardProductPhoto } from "@shared/standard-product-photos";
import {
  templatePhotoAdditionPurpose,
  templatePhotoEligibility,
  templatePhotoSourceUrls,
  MAX_TEMPLATE_PHOTO_REFERENCES,
} from "@shared/template-photo-references";

function toImageRecordsAE(raw: unknown): AdImageRecord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => normalizeExampleImage(entry)).filter((r): r is AdImageRecord => r != null);
}

import {
  BUCKETS, PRESETS, bucketOf, shortNameOf,
  type BucketId, type PresetId,
} from "@/lib/static-ad-buckets";
import { BUCKET_VISUALS, getCategoryVisual, type BucketVisual } from "@/lib/bucket-visuals";
import type { TemplateCategory } from "@shared/schema";

// Upload-date filter for the template picker. Purely client-side: narrows the
// visible templates by each ad unit's existing `createdAt` timestamp.
type DateFilterId = "all" | "7d" | "30d" | "90d";

const DATE_FILTER_OPTIONS: { id: DateFilterId; label: string; days: number | null }[] = [
  { id: "all", label: "All time", days: null },
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

// Returns true when a unit was created within the active window. Units missing a
// timestamp fail any bounded window (we can't confirm they're recent) but always
// pass "All time".
function passesDateFilter(unit: AdUnitType, filter: DateFilterId): boolean {
  const days = DATE_FILTER_OPTIONS.find((o) => o.id === filter)?.days ?? null;
  if (days == null) return true;
  const raw = (unit as { createdAt?: string | Date | null }).createdAt;
  if (!raw) return false;
  const ts = new Date(raw).getTime();
  if (Number.isNaN(ts)) return false;
  return ts >= Date.now() - days * 24 * 60 * 60 * 1000;
}

function createdAtMs(unit: AdUnitType): number {
  const raw = (unit as { createdAt?: string | Date | null }).createdAt;
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

type JobStatus = "queued" | "rendering" | "completed" | "failed" | "cancelled";

interface Job {
  adUnitId: string;
  adUnitName: string;
  // User-set display name overriding the template-name title (null = none).
  customName: string | null;
  status: JobStatus;
  imageUrl: string | null;
  error: string | null;
  // Server-side generated_ads.id; populated when the generation row was
  // created server-side. Local-only retries that bypass the generation
  // endpoint won't have one — Save is disabled in that case.
  generatedAdId?: string;
  isSaved?: boolean;
  saving?: boolean;
  // Per-tile aspect ratio. Ads Express fans out one tile per
  // (ad unit × ratio), so a single ad unit can appear in several ratios.
  aspectRatio: AspectRatio;
  // Task #1705 — true for user-uploaded images (source "manual_upload").
  // These have no template behind them: the tile shows a "Manual upload"
  // badge and hides template-only affordances (Regen / Reframe / Retry).
  isManual?: boolean;
  // Optional persona/product/angle labels shown as chips on manual tiles.
  manualTags?: string[];
  // Template the ad was generated from (adUnitNameSnapshot, falling back to
  // a live ad-unit lookup). Null on manual uploads / unknown templates.
  templateName?: string | null;
}

// Server-side row shapes we care about for polling.
type ServerGenerationStatus = "queued" | "running" | "completed" | "partial" | "failed" | "cancelled" | "needs_review";
interface ServerGeneration {
  id: string;
  brandId: string;
  status: ServerGenerationStatus;
  personaId: string | null;
  productId: string | null;
  offerId?: string | null;
  promotedItemType?: string | null;
  narrativeAngleId: string | null;
  angleNameSnapshot?: string | null;
  projectId: string | null;
  aspectRatio: string | null;
  selectedAdUnitIds: string[] | null;
  salesDiscountId: string | null;
  personaCharacterSelectorEnabled?: boolean | null;
  selectedPersonaCharacterNameSnapshot?: string | null;
}
interface ServerGeneratedAd {
  id: string;
  generationId: string;
  adUnitTypeId: string | null;
  // Template name captured at creation (survives template renames/deletes).
  adUnitNameSnapshot?: string | null;
  customName: string | null;
  // Task #1572 — auto-generated convention name
  // ({Persona}_{Product}_{Angle}_Static_{YYMMDD}{Letter}); null on legacy
  // rows. Preferred over the template name; customName still wins.
  defaultNameSnapshot: string | null;
  aspectRatio: string | null;
  status: "pending" | "rendering" | "queued" | "ready" | "failed" | "cancelled" | "needs_review";
  imageUrlOriginal: string | null;
  // Durable R2 URL, present once the ad is saved. Preferred over the
  // expiring provider URL when editing so older ads stay editable.
  imageUrlSaved: string | null;
  errorMessage: string | null;
  isSaved: boolean;
  isWinner: boolean;
  // Task #1705 — "manual_upload" on user-uploaded rows (no template/run).
  source?: string | null;
  // Task #1705 follow-on — optional tags on manual uploads (name snapshots
  // captured server-side at upload time). Null on generated rows.
  personaNameSnapshot?: string | null;
  productNameSnapshot?: string | null;
  offerNameSnapshot?: string | null;
  creativeAngleNameSnapshot?: string | null;
}

const TERMINAL_GEN_STATUSES: ServerGenerationStatus[] = ["completed", "partial", "failed", "cancelled", "needs_review"];

// Curated list of template slugs that appear in the "BriteCo Templates"
// section. This section only renders for brands whose name/slug matches
// BriteCo (case-insensitive; tolerates the BrightCo spelling too). The
// templates themselves can be brand-scoped or global — they're surfaced from
// whatever the `/ad-units` endpoint returns for the current brand.
const BRITECO_TEMPLATE_SLUGS = [
  "question-hook--human-reaction-service-ad",
  "luxury-hand-jewelry-presentation-system",
  "handwritten-sticky-note-service-hook",
];
const BRITECO_BRAND_RE = /^(brite|bright)\s*co$/i;

// Local UI overrides (Save in flight, inpainted image swap) layered on top
// of polled server state without losing them on the next refetch.
type LocalOverride = Partial<Pick<Job, "imageUrl" | "isSaved" | "saving" | "customName">>;

// Number of templates shown per category before "View More" is needed. The
// picker grid maxes out at 5 columns (lg), so 5 keeps desktop to one row.
const BUCKET_COLLAPSED_COUNT = 5;

const ADS_EXPRESS_IMAGE_ENGINE_OPTIONS: {
  value: AdsExpressImageEngineChoice;
  label: string;
}[] = [
  { value: "gpt-image-2", label: "GPT-Image-2" },
  { value: "flux-2-pro", label: "Flux 2 Pro" },
  { value: "gpt-image-2.5", label: "GPT-Image-2.5" },
];

export function AdsExpressImageEngineSelector({
  brandId,
  choice,
  has916,
  disabled,
  onChoiceChange,
}: {
  brandId: string;
  choice: AdsExpressImageEngineChoice | "";
  has916: boolean;
  disabled: boolean;
  onChoiceChange: (choice: AdsExpressImageEngineChoice | "") => void;
}) {
  const {
    data: brandDefaultImageEngine,
    isLoading: loadingBrandDefaultImageEngine,
    isError: brandDefaultImageEngineError,
    error: brandDefaultImageEngineErrorDetail,
  } = useQuery<{ engine: string | null }>({
    queryKey: [`/api/brands/${brandId}/ads-express-image-engine`],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const brandEngineLabel = brandDefaultImageEngine?.engine
    ? ADS_EXPRESS_IMAGE_ENGINE_OPTIONS.find(
        (option) => option.value === brandDefaultImageEngine.engine,
      )?.label ?? brandDefaultImageEngine.engine
    : null;

  return (
    <div data-testid="panel-ads-express-image-engine">
      <Label className="text-xs font-medium flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        Image engine
      </Label>
      <p className="text-[11px] text-muted-foreground mt-0.5">
        Choose an engine for this run only. Brand default uses the brand&apos;s standard routing
        {brandEngineLabel ? `, with the admin's ${brandEngineLabel} override for 9:16` : ""}.
        {" "}An explicit choice uses that engine for every selected ratio, including the{" "}
        {safeZoneBaseRenderRatio("9:16")} base of 9:16 outputs, even if a brand admin set an override.
        {has916 && " Your selection includes 9:16."}
        {" "}An explicit selection also bypasses automatic GPT-Image-2 routing for person or
        character references; likeness may vary by engine.
      </p>
      {brandDefaultImageEngineError && (
        <p className="text-[11px] text-destructive" role="alert">
          Could not load the brand&apos;s default image engine
          {brandDefaultImageEngineErrorDetail instanceof Error
            ? `: ${brandDefaultImageEngineErrorDetail.message}`
            : "."}
        </p>
      )}
      <Select
        value={choice || "brand-default"}
        onValueChange={(value) =>
          onChoiceChange(
            value === "brand-default" ? "" : (value as AdsExpressImageEngineChoice),
          )
        }
        disabled={disabled || loadingBrandDefaultImageEngine || brandDefaultImageEngineError}
      >
        <SelectTrigger
          className="mt-2 h-8 text-xs w-64"
          data-testid="select-ads-express-image-engine"
        >
          <SelectValue placeholder="Brand default" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="brand-default">
            {`Brand default${brandEngineLabel ? ` (9:16 admin: ${brandEngineLabel})` : ""}`}
          </SelectItem>
          {ADS_EXPRESS_IMAGE_ENGINE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function AdsExpressPage() {
  const imageEditsRevision = usePendingImageEditsRevision();
  const { brandId } = useParams<{ brandId: string }>();
  const search = useSearch();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = !!user?.isMaster || !!user?.isSuperuser;
  const [categoriesManagerOpen, setCategoriesManagerOpen] = useState(false);

  const [personaId, setPersonaId] = useState<string>("");
  const [selectedPersonaCharacterId, setSelectedPersonaCharacterId] = useState("");
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const [productId, setProductId] = useState<string>("");
  // Task #744 — optional product variant selection (null = no specific variant).
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(null);
  const [offerId, setOfferId] = useState<string>("");
  const [angleId, setAngleId] = useState<string>("");
  // Narrative angle source: "saved" picks an existing angle; "custom" lets the
  // user type a free-form concept we transform into a saved angle on Generate
  // (mirrors the UGC create flow).
  const [angleMode, setAngleMode] = useState<"saved" | "custom">("saved");
  const [funnelStage, setFunnelStage] = useState<SavedAngleStage>("all");
  const stageBrandIdRef = useRef(brandId);
  useEffect(() => {
    if (stageBrandIdRef.current !== brandId) {
      stageBrandIdRef.current = brandId;
      setFunnelStage("all");
    }
  }, [brandId]);
  const [concept, setConcept] = useState<string>("");
  // True while the Generate request is in flight (covers the concept→angle
  // transform step in custom mode before the run row appears).
  const [submitting, setSubmitting] = useState(false);
  const [projectId, setProjectId] = useState<string>("");
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set());
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(new Set());
  const [aspectRatios, setAspectRatios] = useState<AspectRatio[]>(() => loadLastAspectRatios());
  const [salesDiscountId, setSalesDiscountId] = useState<string>("");
  // Whether to bake the brand logo into every ad in this run. ON by
  // default = opt-in (logo off unless the user turns it on, or a prior run restores it).
  const [includeLogo, setIncludeLogo] = useState<boolean>(false);

  // ── Advanced settings: visual reference images + additional context.
  // Up to MAX_AD_REFERENCES uploaded images, each with an optional note, that
  // steer the ad's VISUAL ENVIRONMENT only (background/lighting/color/mood).
  // Empty = legacy behaviour (no references → byte-for-byte identical render).
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);
  // Task #812 — each reference carries a purpose: "auto" (default; the server
  // infers product-vs-environment from the image + notes at submit time),
  // "environment" (steer setting/lighting/mood only), or "product" (the
  // depicted product must appear in the ad as the featured product).
  const [references, setReferences] = useState<
    Array<{ url: string; note: string; purpose: "auto" | "environment" | "product" | "product_environment" | "person_identity" | "template_photo"; identityConsent?: boolean; source?: "upload" | "product" | "adgen" | "offer" }>
  >([]);
  const [templatePhotoSource, setTemplatePhotoSource] = useState<"upload" | "product" | "adgen" | "offer">("upload");
  const [pendingLibraryPhotos, setPendingLibraryPhotos] = useState<string[]>([]);
  const [additionalContext, setAdditionalContext] = useState<string>("");
  // Task #1536 — beta Google Font picker ("" = no font → byte-for-byte
  // legacy run; the builder nulls it and the server never touches the flag).
  const [adFontFamily, setAdFontFamily] = useState<string>("");
  // Task #1777 follow-up — this is deliberately in-memory only. Empty means
  // let the brand's current image-engine setting route this run.
  const [imageEngineChoice, setImageEngineChoice] = useState<AdsExpressImageEngineChoice | "">("");
  const [uploadingRef, setUploadingRef] = useState<boolean>(false);
  // Task #1413 — KC-style product reference image selection (beta-gated).
  // FULLY SEPARATE from the upload `references` array above: kept in click
  // order (#1 = the primary product photo attached in the positional product
  // slot) and submitted as `productImageUrls`, which the server snapshots on
  // the run and the render worker uses INSTEAD of the product's default
  // adgenImageRefs/mediaJson resolution. Mirrors ads-express-kc.tsx.
  const [selectedLibraryImages, setSelectedLibraryImages] = useState<string[]>([]);
  const refFileInputRef = useRef<HTMLInputElement | null>(null);

  // Inline reframe tiles — each Reframe click adds a pseudo-tile to the
  // results grid (interleaved next to its source) so the user can keep
  // navigating and kick off more reframes while one is rendering. The
  // server endpoint only renders the image; saving to My Ads is an
  // explicit per-tile click after the image lands.
  type ReframeSource = {
    brandId: string;
    adUnitId: string | null;
    personaId: string | null;
    productId: string | null;
    narrativeAngleId: string | null;
    projectId: string | null;
    parentAdId: string;
    prompt: string | null;
    // Task #1517 — present ONLY when the server's safe_zone_reframe grant
    // is active for this user+brand; when set, the save call passes it
    // through so the persisted image is normalized to exact advertised
    // dimensions and the library row records its ratio.
    aspectRatio?: string | null;
  };
  type ReframeTile = {
    id: string;
    sourceAdUnitId: string;
    // The specific source tile (generated_ads.id) this reframe came from, so
    // reframes attach to the correct tile even when one ad unit appears in
    // several ratios.
    sourceGeneratedAdId: string;
    sourceAdName: string;
    aspectRatio: "1:1" | "4:5" | "9:16";
    status: "loading" | "ready" | "error" | "saving" | "saved";
    imageUrl?: string;
    source?: ReframeSource;
    errorMsg?: string;
  };
  const [reframeTiles, setReframeTiles] = useState<ReframeTile[]>([]);
  const reframeTileSavesInFlight = useRef(new Set<string>());
  // Task #1705 — manual upload tracking. Each picked file gets its own row
  // (per-file progress + per-file failures that never block the batch).
  const [activeGenerationId, setActiveGenerationId] = useState<string | null>(null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, LocalOverride>>({});
  const [lightboxJob, setLightboxJob] = useState<Job | null>(null);
  // Task #1625 — ad notes (`ad_notes` beta grant). Saved jobs get
  // a notes button + count indicator in the lightbox.
  const notesEnabled = useAdNotesEnabled(brandId);
  const noteCounts = useAdNoteCounts(brandId, notesEnabled);
  const [notesDialogAd, setNotesDialogAd] = useState<{ adId: string; adName: string | null } | null>(null);
  // 9:16 → 4:5 safe-zone preview overlay (Task #819) — one page-level toggle
  // shared by the result tiles and the lightbox.
  const [showSafeZone, setShowSafeZone] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editingReframeId, setEditingReframeId] = useState<string | null>(null);
  // Grid tiles whose inline image edit is still running in the BACKGROUND after
  // the edit modal was closed. Drives the "Regenerating…" overlay on the tile and
  // auto-applies the edited image when it lands — so closing the modal mid-edit no
  // longer hides an in-flight edit.
  const [editingTileIds, setEditingTileIds] = useState<Set<string>>(new Set());
  // Coarse server-reported phase per in-flight tile edit (rendering →
  // saving); drives the overlay label alongside editingTileIds.
  const [tileEditPhases, setTileEditPhases] = useState<Record<string, string | null>>({});
  const [tileEditErrors, setTileEditErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilterId>("all");
  const [newestFirst, setNewestFirst] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [selectedTemplateSetId, setSelectedTemplateSetId] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const didMountRef = useRef(false);
  const jobsGridRef = useRef<HTMLDivElement>(null);
  const hydratedFromUrlRef = useRef(false);
  const lastBrandIdRef = useRef<string | undefined>(brandId);
  const pollingTilesRef = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  const editingJobRef = useRef<Job | null>(null);
  const editingReframeIdRef = useRef<string | null>(null);

  const setOverride = (genAdId: string, patch: LocalOverride) => {
    setLocalOverrides((prev) => ({ ...prev, [genAdId]: { ...(prev[genAdId] ?? {}), ...patch } }));
  };

  // Task #1494 — persist an accepted inline edit server-side so it survives
  // navigating away and back (works for saved AND unsaved ads). The optimistic
  // local override has already swapped the tile; this just makes it durable.
  const persistAcceptedEdit = useCallback(async (genAdId: string, newUrl: string) => {
    try {
      await apiRequest("POST", `/api/generated-ads/${genAdId}/accept-edit`, { imageUrl: newUrl });
      invalidateApprovalStates(queryClient, brandId);
      queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
      // Task #2040 — a persisted edit on a saved ad creates an undoable
      // previous version; refresh the undo index.
      invalidateUndoableAds(brandId);
    } catch (e: any) {
      toast({
        title: "Couldn't persist edit",
        description: e?.message ? String(e.message) : "The edit may not survive leaving this page.",
        variant: "destructive",
      });
      throw e;
    }
  }, [activeGenerationId, toast, brandId]);

  // Task #2040 — ads with an undoable last edit (saved tiles only show the
  // button; availability is server-driven and drops once the superseded
  // object is purged).
  const undoableAdIds = useUndoableAdIds(brandId);
  const redoableAdIds = useRedoableAdIds(brandId);

  const markTileEditing = useCallback((id: string, on: boolean) => {
    if (on) setTileEditErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
    if (!on) {
      setTileEditPhases((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    setEditingTileIds((prev) => {
      if (on === prev.has(id)) return prev;
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Keep refs in sync so the background edit poller can read the LIVE modal
  // state (is the edit modal currently open on this tile?) without being
  // re-created, and bail on unmount without clearing the persisted pending job.
  useEffect(() => { editingJobRef.current = editingJob; }, [editingJob]);
  useEffect(() => { editingReframeIdRef.current = editingReframeId; }, [editingReframeId]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  // Task #1609 — the brand's saved Design System fonts that can steer ads
  // (Google-source only, via the shared isAdSteerableBrandFont helper).
  // Surfaced as a "Brand fonts" group at the top of the Ad font picker.
  // De-duplicated by family; role label (Headline / Body / …) shown when set.
  // Picker-UI only: option values resolve to plain family names so the submit
  // payload / snapshot / render path is untouched.
  const brandAdFonts = useMemo(() => {
    const fonts = ((brand as any)?.brandDesignSystem?.fonts ?? []) as BrandFont[];
    const seen = new Set<string>();
    const out: { family: string; roleLabel: string | null }[] = [];
    for (const f of fonts) {
      if (!isAdSteerableBrandFont(f) || seen.has(f.family)) continue;
      seen.add(f.family);
      const role = brandFontRoleFromLabel(f.label);
      const roleLabel =
        role === "none"
          ? null
          : BRAND_FONT_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? null;
      out.push({ family: f.family, roleLabel });
    }
    return out;
  }, [brand]);

  const { data: personas = [], isLoading: loadingPersonas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  const { data: salesDiscounts = [] } = useQuery<SalesDiscount[]>({
    queryKey: [`/api/brands/${brandId}/sales-discounts`],
    enabled: !!brandId,
  });

  const { data: products = [], isLoading: loadingProducts } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const { data: offers = [], isLoading: loadingOffers } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });

  const { data: angles = [], isLoading: loadingAngles } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`],
    enabled: !!brandId,
  });

  const { data: adUnits = [], isLoading: loadingAdUnits } = useQuery<AdUnitCatalogItem[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
    enabled: !!brandId,
  });

  const { data: staticTemplateSets = [] } = useQuery<Array<StaticTemplateSet & { members: StaticTemplateSetMember[] }>>({
    queryKey: [`/api/brands/${brandId}/static-template-sets`],
    enabled: !!brandId,
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: [`/api/brands/${brandId}/projects`],
    enabled: !!brandId,
  });

  // Admin-managed custom categories. Templates assigned to a category group
  // under it (in displayOrder); unassigned templates fall back to the legacy
  // bucket/heuristic grouping below.
  // Task #1331 — beta flags for the CURRENT user on this brand. Reference
  // images are gated behind `reference_images_enabled` (master-managed on
  // /admin/beta-features); default OFF, live via query invalidation.
  const { data: betaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId,
    // Feature settings can change outside this mounted page (for example in
    // Admin or by a rollout task). The global QueryClient caches forever and
    // disables focus refetches, which otherwise leaves a previously cached
    // false/missing flag hiding newly enabled controls for the whole SPA
    // session.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const referenceImagesEnabled = betaFlags?.reference_images_enabled === true;
  const templatePhotoFeatures = templatePhotoEligibility(brandId, betaFlags ?? {});
  const narrativeFunnelStageEnabled = betaFlags?.narrative_funnel_stage_enabled === true;
  // Task #2665 — separate, default-off BRAND gate for exact identity
  // preservation. Ordinary reference images remain available independently.
  const personIdentityReferencesEnabled =
    betaFlags?.ads_person_identity_references === true;
  // Task #2117 — user-granted run-queue flag (master-managed, fail-closed).
  // ON: the form stays usable while a run is in progress and additional
  // Generate clicks enqueue further runs (FIFO). OFF: today's locked-form
  // single-active-run behavior, byte-for-byte.
  const runQueueEnabled = betaFlags?.ads_express_run_queue === true;
  // Task #1413 — "Choose from library" product-photo picker; a second,
  // purely-client-side entry point into the same `references` array the
  // upload flow populates (standard Ads Express only, not KC/instant).
  const productRefLibraryEnabled = betaFlags?.product_reference_library_enabled === true;
  // Task #1536 — Google Font picker, gated by the current user's grant.
  const googleFontAdsEnabled = betaFlags?.google_font_ads === true;
  const adsExpressImageEngineChoiceEnabled =
    betaFlags?.[ADS_EXPRESS_IMAGE_ENGINE_SELECTOR_FLAG] === true;
  const personaCharacterSelectorEnabled =
    betaFlags?.ads_persona_character_selector === true;
  const { data: budgetBrandEngine, isLoading: budgetEngineLoading, isError: budgetEngineError } =
    useQuery<{ engine: "gpt-image-2" | "flux-2-pro" | null }>({
      queryKey: [`/api/brands/${brandId}/ads-express-image-engine`],
      enabled: !!brandId, staleTime: 0, refetchOnMount: "always",
    });

  // Task #1654 — master-only cross-brand template browser. DOUBLE-gated:
  // the `cross_brand_ad_templates_enabled` grant AND user.isMaster —
  // deliberately NOT the
  // file's broader isAdmin (superusers are excluded by spec). Gated off ⇒
  // the template section renders exactly as today (no tabs, no new queries).
  const isMaster = user?.isMaster === true;
  const crossBrandTemplatesEnabled = betaFlags?.cross_brand_ad_templates_enabled === true;
  const crossBrandTabsEnabled = isMaster && crossBrandTemplatesEnabled;
  // Task #2046 follow-up — the enriched Recent generations table is DOUBLE-
  // gated: user.isMaster AND the GLOBAL (not per-brand) recentGenerationsPanel
  // switch on /api/admin/ads-express-settings (admin page toggle). Gated
  // off ⇒ masters see the standard Previous Sessions panel like everyone.
  const { data: adsExpressGlobalSettings } = useQuery<{ recentGenerationsPanel?: boolean }>({
    queryKey: ["/api/admin/ads-express-settings"],
    enabled: isMaster,
  });
  const recentGenerationsPanelEnabled =
    isMaster && adsExpressGlobalSettings?.recentGenerationsPanel === true;
  // Task #1654 — which template browser view is showing: the current
  // brand's own section (default) or the cross-brand "Other brands" one.
  const [showOtherBrands, setShowOtherBrands] = useState(false);
  // "All brands" tab filters: a multi-select brand filter (null = every
  // other brand — the default) plus search/date/sort controls that mirror
  // Tab 1's toolbar but are deliberately INDEPENDENT state, so filtering
  // one tab never invisibly changes the other.
  const [foreignBrandFilter, setForeignBrandFilter] = useState<Set<string> | null>(null);
  const [foreignSearchTerm, setForeignSearchTerm] = useState("");
  const [foreignDateFilter, setForeignDateFilter] = useState<DateFilterId>("all");
  const [foreignNewestFirst, setForeignNewestFirst] = useState(false);
  const [expandedForeignBrands, setExpandedForeignBrands] = useState<Set<string>>(new Set());
  // Session-local memory of every FOREIGN template row the master has
  // selected (appended on check, never pruned — see toggleForeignTemplate)
  // so id → row display lookups (job tiles, selected-summary chip) keep
  // working after the row is filtered out of view.
  const [foreignUnitsById, setForeignUnitsById] = useState<Map<string, AdUnitType>>(new Map());
  const { data: allBrands = [], isLoading: loadingAllBrands } = useQuery<Brand[]>({
    queryKey: ["/api/admin/brands"],
    enabled: crossBrandTabsEnabled,
  });
  // ONE master-gated fetch of every ad unit across all brands powers the
  // whole tab (the endpoint already normalizes example images); globals and
  // the current brand's rows are dropped client-side below.
  const { data: allAdminUnits = [], isLoading: loadingAllAdminUnits } = useQuery<AdUnitType[]>({
    queryKey: ["/api/admin/ad-units"],
    enabled: crossBrandTabsEnabled,
  });

  const { data: templateCategories = [] } = useQuery<TemplateCategory[]>({
    queryKey: ["/api/template-categories"],
  });

  // Mirror the Static Ad Units page filter (everything that isn't UGC or
  // video) so any template visible there is also pickable in Ads Express.
  // Previously this was hard-pinned to formatType === "static", which hid
  // legitimate static-image templates created with format types like
  // "meme", "mixed-media", or "photographic".
  // BriteCo-only curated section. Matches the brand by name OR slug
  // (case-insensitive, tolerates the BrightCo spelling) so admin-renamed
  // brands still trigger the section. The slug list is intentionally
  // hardcoded — these are curated picks. The three slugs in
  // BRITECO_TEMPLATE_SLUGS are hidden everywhere for non-BriteCo brands and
  // only appear inside the dedicated "BriteCo Templates" bucket section for
  // BriteCo (not duplicated into the standard buckets below).
  const isBriteCoBrand = useMemo(() => {
    if (!brand) return false;
    return BRITECO_BRAND_RE.test(brand.name ?? "") || BRITECO_BRAND_RE.test(brand.slug ?? "");
  }, [brand]);
  const briteCoSlugSet = useMemo(() => new Set(BRITECO_TEMPLATE_SLUGS), []);

  const staticAdUnits = useMemo(
    () =>
      adUnits.filter(
        (u) =>
          u.status !== "archived" &&
          u.formatType !== "ugc" &&
          u.formatType !== "video" &&
          u.formatType !== "pixar" &&
          // BriteCo-curated templates are restricted to the BriteCo brand and
          // hidden from every other brand's library, count, presets, and
          // search results.
          (isBriteCoBrand || !(u.slug && briteCoSlugSet.has(u.slug))) &&
          // Templates with no usable example image hard-fail at render time. A
          // regular user can't fix that (editing ad units is admin-only), so
          // hide them entirely; admins still see them (flagged, with a link to
          // add an image) so the library gap stays actionable.
          (isAdmin || hasUsableExampleImage(u)),
      ),
    [adUnits, isBriteCoBrand, briteCoSlugSet, isAdmin],
  );

  // Approval is a browsing preference, not an access rule. Keep
  // `staticAdUnits` as the complete accessible/resource pool for display
  // lookups, existing selections and generation; only this derived pool feeds
  // the standard template browser's filters, presets, counts and select-all.
  const browseAdUnits = useMemo(
    () => getAdsExpressBrowsePool(staticAdUnits, showAllTemplates),
    [staticAdUnits, showAllTemplates],
  );
  const hiddenUnapprovedCount = countHiddenAdsExpressTemplates(staticAdUnits);

  const briteCoTemplates = useMemo(() => {
    if (!isBriteCoBrand) return [] as AdUnitCatalogItem[];
    const bySlug = new Map(browseAdUnits.filter((u) => !!u.slug).map((u) => [u.slug!, u]));
    const list = BRITECO_TEMPLATE_SLUGS
      .map((slug) => bySlug.get(slug))
      .filter((u): u is AdUnitCatalogItem => !!u)
      .filter((u) => passesDateFilter(u, dateFilter));
    if (newestFirst) list.sort((a, b) => createdAtMs(b) - createdAtMs(a));
    return list;
  }, [isBriteCoBrand, browseAdUnits, dateFilter, newestFirst]);

  // Ad units missing a usable example image hard-fail at render time, so
  // they're treated as ineligible: they can't be selected and block a run.
  // Non-admins never see these (they're filtered out of `staticAdUnits`
  // above), so in practice this set is only non-empty for admins — whose
  // cards show the flag plus a link to add an image.
  const ineligibleTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    staticAdUnits.forEach((u) => {
      if (!hasUsableExampleImage(u)) ids.add(u.id);
    });
    return ids;
  }, [staticAdUnits]);

  const eligibleTemplateCount = browseAdUnits.filter((u) => !ineligibleTemplateIds.has(u.id)).length;
  const selectedBrowseTemplateCount = browseAdUnits.reduce(
    (count, unit) => count + (selectedTemplateIds.has(unit.id) ? 1 : 0),
    0,
  );
  const selectedAccessibleTemplateCount = staticAdUnits.reduce(
    (count, unit) => count + (selectedTemplateIds.has(unit.id) ? 1 : 0),
    0,
  );

  // Task #1654 — "All brands" tab data. The dropdown excludes the current
  // brand (Tab 1 already covers it). The pool keeps only rows a foreign
  // brand OWNS — global templates already render in Tab 1 and must not
  // duplicate here — with Tab 1's static/archived format filters applied so
  // the same kinds of templates are pickable in both tabs.
  const otherBrands = useMemo(
    () =>
      allBrands
        .filter((b) => b.id !== brandId)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [allBrands, brandId],
  );
  const foreignPool = useMemo(
    () =>
      allAdminUnits.filter(
        (u) =>
          !!u.brandId &&
          u.brandId !== brandId &&
          u.status !== "archived" &&
          u.formatType !== "ugc" &&
          u.formatType !== "video" &&
          u.formatType !== "pixar",
      ),
    [allAdminUnits, brandId],
  );
  const foreignSelectedCount = useMemo(
    () => foreignPool.reduce((n, u) => n + (selectedTemplateIds.has(u.id) ? 1 : 0), 0),
    [foreignPool, selectedTemplateIds],
  );
  // Brand-grouped sections mirroring Tab 1's bucket sections: brand filter
  // (null = all), then the same search/date narrowing, newest-first sort
  // within each brand, empty groups dropped. Group order = otherBrands
  // (alphabetical).
  const foreignBrandGroups = useMemo(() => {
    const q = foreignSearchTerm.trim().toLowerCase();
    const matchesSearch = (u: AdUnitType) => {
      if (!q) return true;
      const short = shortNameOf(u.slug ?? "", u.name).toLowerCase();
      return short.includes(q) || (u.name ?? "").toLowerCase().includes(q);
    };
    const byBrand = new Map<string, AdUnitType[]>();
    foreignPool.forEach((u) => {
      if (foreignBrandFilter && !foreignBrandFilter.has(u.brandId!)) return;
      if (!matchesSearch(u)) return;
      if (!passesDateFilter(u, foreignDateFilter)) return;
      const list = byBrand.get(u.brandId!);
      if (list) list.push(u);
      else byBrand.set(u.brandId!, [u]);
    });
    if (foreignNewestFirst) {
      byBrand.forEach((units) => units.sort((a, b) => createdAtMs(b) - createdAtMs(a)));
    }
    return otherBrands
      .map((b) => ({ brand: b, units: byBrand.get(b.id) ?? [] }))
      .filter((g) => g.units.length > 0);
  }, [foreignPool, otherBrands, foreignBrandFilter, foreignSearchTerm, foreignDateFilter, foreignNewestFirst]);
  const foreignVisibleCount = useMemo(
    () => foreignBrandGroups.reduce((sum, g) => sum + g.units.length, 0),
    [foreignBrandGroups],
  );
  // Union of the current brand's templates + any REMEMBERED foreign picks,
  // used wherever an id must resolve to a template row for DISPLAY (job
  // tiles, selected-summary chip). With the gate off the map is always
  // empty, so this IS staticAdUnits — byte-identical behavior.
  const templatesForDisplay = useMemo(() => {
    if (foreignUnitsById.size === 0) return staticAdUnits;
    const staticIds = new Set(staticAdUnits.map((u) => u.id));
    const extras: AdUnitType[] = [];
    foreignUnitsById.forEach((u) => {
      if (!staticIds.has(u.id)) extras.push(u);
    });
    return extras.length > 0 ? [...staticAdUnits, ...extras] : staticAdUnits;
  }, [staticAdUnits, foreignUnitsById]);
  const selectedTemplatesIncludeMeme = useMemo(
    () => templatesForDisplay.some(
      (unit) => selectedTemplateIds.has(unit.id) && unit.isMeme === true,
    ),
    [templatesForDisplay, selectedTemplateIds],
  );
  const characterPickerAvailable =
    personaCharacterSelectorEnabled && !selectedTemplatesIncludeMeme;

  const persona = personas.find((p) => p.id === personaId) ?? null;
  const { data: personaCharactersData, isLoading: loadingPersonaCharacters } =
    useQuery<PersonaCharacter[]>({
      queryKey: [`/api/personas/${personaId}/characters`],
      enabled: (characterPickerAvailable || betaFlags?.ads_persona_character_reference_sheet === true) && !!personaId,
      refetchOnMount: "always",
      refetchInterval: (query) => {
        const characters =
          (query.state.data as PersonaCharacter[] | undefined) ?? [];
        return characters.some(
          (character) =>
            character.avatarStatus === "pending" ||
            character.avatarStatus === "generating",
        )
          ? 3000
          : false;
      },
    });
  const personaCharacters = Array.isArray(personaCharactersData)
    ? personaCharactersData
    : [];
  const readyPersonaCharacters = personaCharacters.filter(
    (character) => character.avatarStatus === "ready" && !!character.avatarUrl,
  );
  const selectedPersonaCharacter = readyPersonaCharacters.find(
    (character) => character.id === selectedPersonaCharacterId,
  );
  const unavailablePersonaCharacterCount =
    personaCharacters.length - readyPersonaCharacters.length;

  useEffect(() => {
    setSelectedPersonaCharacterId("");
  }, [personaId]);

  useEffect(() => {
    if (!characterPickerAvailable || !persona || loadingPersonaCharacters) {
      if (!characterPickerAvailable) setSelectedPersonaCharacterId("");
      return;
    }
    if (readyPersonaCharacters.some(
      (character) => character.id === selectedPersonaCharacterId,
    )) return;
    const next =
      readyPersonaCharacters.find(
        (character) => character.id === persona.primaryCharacterId,
      ) ?? readyPersonaCharacters[0];
    setSelectedPersonaCharacterId(next?.id ?? "");
  }, [
    characterPickerAvailable,
    loadingPersonaCharacters,
    persona,
    readyPersonaCharacters,
    selectedPersonaCharacterId,
  ]);
  const product = products.find((p) => p.id === productId) ?? null;
  const offer = offers.find((o) => o.id === offerId) ?? null;
  const isOfferMode = promotedItemType === "offer";

  // Task #1413 — the selected product's own photo library (mediaJson +
  // adgenImageRefs), deduped by URL, non-string/empty entries filtered out.
  // Feeds the beta "Choose from library" reference picker only.
  // Grouped so the picker can label + lead with the curated Ad Gen Images
  // (the product's generation anchors) ahead of the general product photos.
  // A URL present in both lists is shown once, under Ad Gen Images.
  // Task #1426 — offer runs feed the SAME picker from the selected offer's
  // adgenImageRefs (offers have no mediaJson field, so the media group stays
  // empty and the picker shows only Ad Gen Images). One flag gates both.
  const productLibraryGroups = useMemo(() => {
    const item = isOfferMode ? offer : product;
    if (!item) return { adgen: [] as string[], media: [] as string[] };
    const p = item as { mediaJson?: unknown; adgenImageRefs?: unknown };
    const collect = (raw: unknown): string[] =>
      Array.isArray(raw)
        ? raw.filter((u): u is string => typeof u === "string" && u.trim().length > 0)
        : [];
    const seen = new Set<string>();
    const dedupe = (urls: string[]): string[] => {
      const out: string[] = [];
      for (const u of urls) {
        if (seen.has(u)) continue;
        seen.add(u);
        out.push(u);
      }
      return out;
    };
    const adgen = dedupe(collect(p.adgenImageRefs));
    const media = dedupe(collect(isOfferMode ? null : (p as { mediaJson?: unknown }).mediaJson));
    return { adgen, media };
  }, [product, offer, isOfferMode]);
  const productLibraryImages = useMemo(
    () => [...productLibraryGroups.adgen, ...productLibraryGroups.media],
    [productLibraryGroups],
  );
  const resetTemplatePhotoSources = useCallback(() => {
    setTemplatePhotoSource("upload");
    setPendingLibraryPhotos([]);
  }, []);
  const lastPhotoSourceAttachment = useRef(`${brandId ?? ""}:${promotedItemType}:${productId}:${offerId}`);
  useEffect(() => {
    const attachmentKey = `${brandId ?? ""}:${promotedItemType}:${productId}:${offerId}`;
    if (lastPhotoSourceAttachment.current !== attachmentKey) {
      lastPhotoSourceAttachment.current = attachmentKey;
      resetTemplatePhotoSources();
    }
  }, [brandId, promotedItemType, productId, offerId, resetTemplatePhotoSources]);
  useEffect(() => {
    if (!templatePhotoFeatures.libraryEnabled) resetTemplatePhotoSources();
    if (!templatePhotoFeatures.enabled) {
      setReferences((previous) => previous.some((ref) => ref.purpose === "template_photo")
        ? previous.filter((ref) => ref.purpose !== "template_photo")
        : previous);
    }
  }, [templatePhotoFeatures.enabled, templatePhotoFeatures.libraryEnabled, resetTemplatePhotoSources]);

  const templatePhotoCount = references.filter((ref) => ref.purpose === "template_photo").length;
  const ordinaryReferenceCount = references.length - templatePhotoCount;
  const productPhotoSources = isOfferMode
    ? { product: [] as string[], adgen: productLibraryGroups.adgen, offer: productLibraryGroups.adgen }
    : { product: templatePhotoSourceUrls(product?.mediaJson), adgen: productLibraryGroups.adgen, offer: [] as string[] };
  const templatePhotoLibraryItems = templatePhotoSource === "product"
    ? productPhotoSources.product
    : templatePhotoSource === "adgen"
      ? productPhotoSources.adgen
      : templatePhotoSource === "offer"
        ? productPhotoSources.offer
        : [];
  const calculateImageBudgets = useCallback((selectedReferences: typeof references) => {
    const selected = templatesForDisplay.filter((unit) => selectedTemplateIds.has(unit.id));
    const brandHasLogo = Boolean(
      brand?.adgenLogoUrl || brand?.logoUrl,
    );
    return selected.flatMap((unit) => aspectRatios.map((ratio) => {
      const item = isOfferMode ? offer : product;
      const includeProduct = unit.adgenIncludeProductImage !== false && Boolean(item);
      const variantRows = product?.variantsJson ?? [];
      const selectedVariantImage = (Array.isArray(variantRows) ? variantRows : []).find(
        (variant: any) => variant?.name === selectedVariantName,
      )?.imageUrl ?? "";
      const productPhotoInput = {
        includeAllSelectedProductPhotos: true,
        productImageUrlsOverride: productRefLibraryEnabled && selectedLibraryImages.length > 0
          ? selectedLibraryImages : undefined,
      };
      const primaryProductPhoto = includeProduct
        ? resolvePrimaryStandardProductPhoto(productPhotoInput, item, isOfferMode ? "" : selectedVariantImage)
        : "";
      const extraProductPhotos = includeProduct
        ? resolveExtraStandardProductPhotos(productPhotoInput, item, isOfferMode ? "" : selectedVariantImage)
        : [];
      const slotArgs = {
        includeProduct,
        primaryProductPhoto,
        extraProductPhotoCount: extraProductPhotos.length,
        includeLogo: includeLogo && brandHasLogo && (unit.isMeme || betaFlags?.exact_logo_overlay !== true),
        includeFont: !unit.isMeme && Boolean(adFontFamily && googleFontAdsEnabled),
        references: selectedReferences,
      };
      const character = personaCharacterSelectorEnabled
        ? selectedPersonaCharacter
        : personaCharacters.find((candidate) => candidate.id === persona?.primaryCharacterId);
      const plan = planStandardAdImageBudget({
        ...slotArgs, name: unit.name, ratio, isMeme: unit.isMeme === true,
        userEngine: adsExpressImageEngineChoiceEnabled && imageEngineChoice ? imageEngineChoice : null,
        adminEngine: budgetBrandEngine?.engine,
        identityPackEligible: betaFlags?.ads_persona_character_reference_sheet === true && Boolean(character?.avatarUrl),
      });
      return {
        ...plan, id: `${unit.id}-${ratio}`, name: `${unit.name} (${ratio})`,
        imageSlotsUsed: plan.used,
        primaryProductSlots: Number(includeProduct && !!primaryProductPhoto),
        extraProductSlots: extraProductPhotos.length,
        logoSlots: Number(slotArgs.includeLogo),
        fontSlots: Number(slotArgs.includeFont),
      };
    }));
  }, [
    templatesForDisplay, selectedTemplateIds, brand, aspectRatios, imageEngineChoice, budgetBrandEngine,
    adsExpressImageEngineChoiceEnabled,
    isOfferMode, product, offer, selectedVariantName, productRefLibraryEnabled, selectedLibraryImages,
    includeLogo, adFontFamily, googleFontAdsEnabled, betaFlags, personaCharacters,
    personaCharacterSelectorEnabled, selectedPersonaCharacter, persona,
  ]);
  const templatePhotoSlotPlans = useMemo(() => calculateImageBudgets(references), [calculateImageBudgets, references]);
  const explicitBudgetEngineSelected = adsExpressImageEngineChoiceEnabled && !!imageEngineChoice;
  const imageBudgetError = adReferenceCapacityError(references) ||
    templatePhotoSlotPlans.find(plan => plan.error)?.error ||
    (!explicitBudgetEngineSelected && budgetEngineError ? "Could not load the brand image engine. Retry before generating." : null);
  const remainingReferenceCapacity = Math.min(MAX_AD_REFERENCES - ordinaryReferenceCount,
    ...templatePhotoSlotPlans.map(plan => plan.remaining));
  const referenceSelectionError = (candidate: typeof references) =>
    adReferenceCapacityError(candidate) || calculateImageBudgets(candidate).find(plan => plan.error)?.error;

  const addTemplatePhotoLibrarySelection = () => {
    const additions = pendingLibraryPhotos;
    if (!additions.length) return;
    const purpose = templatePhotoAdditionPurpose(isOfferMode, additions.length);
    const source: "offer" | "adgen" | "product" = templatePhotoSource === "offer"
      ? "offer"
      : templatePhotoSource === "adgen"
        ? "adgen"
        : "product";
    setReferences((previous) => {
      const batch = additions.filter(url => !previous.some(ref => ref.url === url))
        .map(url => ({ url, note: "", purpose, ...(templatePhotoFeatures.enabled ? { source } : {}) }));
      const batchError = referenceSelectionError([...previous, ...batch]);
      if (batchError) {
        toast({ title: "Photo limit reached", description: batchError, variant: "destructive" });
        return previous;
      }
      const next = [...previous];
      for (const url of additions) {
        if (next.some((ref) => ref.url === url)) continue;
        const candidate = [
          ...next,
          { url, note: "", purpose, ...(templatePhotoFeatures.enabled ? { source } : {}) },
        ];
        if (referenceSelectionError(candidate)) {
          toast({
            title: "Photo limit reached",
            description: referenceSelectionError(candidate) ?? "Remove a photo before adding more.",
            variant: "destructive",
          });
          break;
        }
        next.push({ url, note: "", purpose, ...(templatePhotoFeatures.enabled ? { source } : {}) });
      }
      return next;
    });
    setPendingLibraryPhotos([]);
  };

  // Task #1413 — when the user picks a different product, default to the
  // first available library image as the primary reference (KC parity; the
  // first grid image IS the legacy default photo, so an untouched selection
  // renders identically to a legacy run). They can add more / reorder by
  // clicking thumbnails.
  const firstLibraryImage = productLibraryImages[0] ?? null;
  useEffect(() => {
    setSelectedLibraryImages(firstLibraryImage ? [firstLibraryImage] : []);
  }, [isOfferMode ? offer?.id : product?.id, firstLibraryImage]);

  const promotedItemId = isOfferMode ? offerId : productId;

  // Brand-unique example for the custom-concept box. A narrative angle is a
  // belief shift — a moment where a persona's old conviction gives way — so
  // the example is framed around the currently selected persona + promoted
  // item, falling back to the brand name and then a generic prompt.
  const conceptPlaceholder = useMemo(() => {
    const who = persona?.name?.trim();
    const thing = (isOfferMode ? offer?.name : product?.name)?.trim();
    const brandName = brand?.name?.trim();
    if (who && thing) {
      return `e.g., ${who} stops settling for "good enough" the moment ${thing} proves it actually delivers…`;
    }
    if (thing) {
      return `e.g., The skeptic who's been burned before finally trusts ${thing} after seeing the proof…`;
    }
    if (brandName) {
      return `e.g., The customer who almost gave up rediscovers why ${brandName} is worth believing in…`;
    }
    return "e.g., A skeptical buyer hits the moment they realize this is the one that finally works…";
  }, [persona, product, offer, brand, isOfferMode]);

  // Narrative angles in the dropdown must match the currently selected
  // persona AND promoted item (product OR offer). Archived angles are
  // excluded. Angles store either productId or offeringId depending on
  // which item they were generated for.
  const availableAngles = useMemo(
    () =>
      angles.filter((a) => narrativeAngleMatchesAdsExpressAttachment(a, {
        personaId,
        promotedItemType,
        productId,
        offerId,
      })),
    [angles, personaId, productId, offerId, isOfferMode],
  );

  const savedAngleChoices = useMemo(
    () => filterSavedAnglesByStage(availableAngles, funnelStage, narrativeFunnelStageEnabled),
    [availableAngles, funnelStage, narrativeFunnelStageEnabled],
  );
  const angle = savedAngleChoices.find((a) => a.id === angleId) ?? null;

  const changeFunnelStage = (stage: SavedAngleStage) => {
    if (!preservingHistoricalAttachment && angleId && !filterSavedAnglesByStage(availableAngles, stage, narrativeFunnelStageEnabled)
      .some((a) => a.id === angleId)) setAngleId("");
    setFunnelStage(stage);
  };

  // Group the angle options by Opportunity Group (`family`) for the dropdown.
  const angleGroups = useMemo(
    () =>
      groupAnglesByFamily(savedAngleChoices).map((g) => ({
        label: g.label,
        options: g.angles.map((a) => ({
          value: a.id,
          label: a.name,
          description: a.summary ?? null,
        })),
      })),
    [savedAngleChoices],
  );

  const { filterByAngles, productsWithAngles, offersWithAngles,
    preserveHydratedAttachment, releaseHistoricalAttachment, preservingHistoricalAttachment } = useAdsExpressItemFilter({
    brandId, activeGenerationId,
    attachment: { personaId, promotedItemType, productId, offerId, angleId },
    angles, loadingAngles, angleMode, savedAngleChoices,
    setProductId, setOfferId, setAngleId,
  });
  const eligibleProducts = filterByAngles ? products.filter((p) => productsWithAngles.has(p.id)) : products;
  const eligibleOffers = filterByAngles ? offers.filter((o) => offersWithAngles.has(o.id)) : offers;
  const promotedItemFilterEmpty = filterByAngles && (isOfferMode
    ? offers.length > 0 && eligibleOffers.length === 0
    : products.length > 0 && eligibleProducts.length === 0);
  const promotedItemEmptyLabel = isOfferMode
    ? "No offers with angles for this persona yet"
    : "No products with angles for this persona yet";

  // Note: `isRunning` is declared further down (derived from the polled
  // generation status). We allow the Generate button to compute its own
  // running-check inline against the same data source to avoid a forward ref.
  const canGenerate = !imageBudgetError && !(!explicitBudgetEngineSelected && budgetEngineLoading) && canGenerateAds({
    persona,
    isOfferMode,
    offer,
    product,
    angleMode,
    concept,
    angle,
    selectedTemplateCount: selectedTemplateIds.size,
  });

  const toggleTemplate = (id: string) => {
    // Ineligible templates (no example image) can never be added; allow
    // removing one only if it somehow ended up selected.
    if (ineligibleTemplateIds.has(id) && !selectedTemplateIds.has(id)) return;
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Task #1654 — Tab 2 card handler: remember the foreign row for display
  // lookups, then run the SAME toggle as Tab 1 (one shared selection Set,
  // so caps/eligibility/generate treat both tabs identically). Foreign
  // templates without a usable example image are never addable — their
  // card renders ineligible and this guard backs that up.
  const toggleForeignTemplate = (u: AdUnitType) => {
    if (!hasUsableExampleImage(u) && !selectedTemplateIds.has(u.id)) return;
    setForeignUnitsById((prev) => {
      if (prev.has(u.id)) return prev;
      const next = new Map(prev);
      next.set(u.id, u);
      return next;
    });
    toggleTemplate(u.id);
  };

  // Brand multi-select semantics: null = all brands (default). Unchecking
  // from "all" materializes the full set minus that brand; re-checking the
  // last missing brand collapses back to null so brands created later are
  // included automatically.
  const toggleForeignBrandChecked = (id: string) => {
    setForeignBrandFilter((prev) => {
      const base = prev === null ? new Set(otherBrands.map((b) => b.id)) : new Set(prev);
      if (base.has(id)) base.delete(id);
      else base.add(id);
      return base.size >= otherBrands.length ? null : base;
    });
  };

  const toggleForeignBrandExpanded = (id: string) => {
    setExpandedForeignBrands((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleBucketExpanded = (id: string) => {
    setExpandedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearAll = () => {
    setSelectedTemplateIds(new Set());
  };

  const applyPreset = (presetId: PresetId) => {
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    const matched = browseAdUnits.filter(
      (u) => u.slug && preset.slugs.includes(u.slug) && !ineligibleTemplateIds.has(u.id),
    );
    const matchedIds = matched.map((u) => u.id);
    setSelectedTemplateIds((prev) => {
      const next = addMode ? new Set(prev) : new Set<string>();
      matchedIds.forEach((id) => next.add(id));
      return next;
    });
    if (matched.length === 0) {
      toast({
        title: "No templates matched",
        description: "This brand's library doesn't include any templates from that preset.",
        variant: "destructive",
      });
    }
  };

  const selectedSlugs = useMemo(() => {
    const set = new Set<string>();
    browseAdUnits.forEach((u) => {
      if (u.slug && selectedTemplateIds.has(u.id)) set.add(u.slug);
    });
    return set;
  }, [browseAdUnits, selectedTemplateIds]);

  const activePreset: PresetId | null = useMemo(() => {
    for (const preset of PRESETS) {
      if (preset.slugs.length !== selectedSlugs.size) continue;
      if (preset.slugs.every((s) => selectedSlugs.has(s))) return preset.id;
    }
    return null;
  }, [selectedSlugs]);

  const grouped = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const categoryById = new Map(templateCategories.map((c) => [c.id, c]));
    const matchesSearch = (u: AdUnitType) => {
      if (!q) return true;
      const slug = u.slug ?? "";
      const short = shortNameOf(slug, u.name).toLowerCase();
      const orig = (u.name ?? "").toLowerCase();
      const bucket = BUCKETS.find((b) => b.id === bucketOf(slug, u.name, (u as { adsExpressBucket?: string | null }).adsExpressBucket))?.label.toLowerCase() ?? "";
      // Include the assigned custom category name in the search haystack.
      const catId = (u as { categoryId?: string | null }).categoryId;
      const category = (catId ? categoryById.get(catId)?.name : "")?.toLowerCase() ?? "";
      return short.includes(q) || orig.includes(q) || bucket.includes(q) || category.includes(q);
    };

    // Bucket holds the legacy/heuristic groups; categoryMap holds assigned ones.
    const bucketMap = new Map<BucketId, AdUnitType[]>();
    BUCKETS.forEach((b) => bucketMap.set(b.id, []));
    const categoryMap = new Map<string, AdUnitType[]>();
    templateCategories.forEach((c) => categoryMap.set(c.id, []));

    browseAdUnits.forEach((u) => {
      if (!matchesSearch(u)) return;
      // Narrow by upload date (client-side, using the existing createdAt).
      if (!passesDateFilter(u, dateFilter)) return;
      // BriteCo-curated templates render in their own dedicated section at
      // the top of the grid for BriteCo brands — skip them here so they
      // don't ALSO appear inside one of the standard buckets.
      if (u.slug && briteCoSlugSet.has(u.slug)) return;
      // Assigned category wins over the legacy bucket grouping.
      const catId = (u as { categoryId?: string | null }).categoryId;
      if (catId && categoryMap.has(catId)) {
        categoryMap.get(catId)!.push(u);
        return;
      }
      const b = bucketOf(u.slug, u.name, (u as { adsExpressBucket?: string | null }).adsExpressBucket);
      bucketMap.get(b)!.push(u);
    });

    if (newestFirst) {
      categoryMap.forEach((units) => units.sort((a, b) => createdAtMs(b) - createdAtMs(a)));
      bucketMap.forEach((units) => units.sort((a, b) => createdAtMs(b) - createdAtMs(a)));
    }

    type Group = { id: string; label: string; description: string; visual: BucketVisual; units: AdUnitType[] };
    const categoryGroups: Group[] = templateCategories.map((c) => ({
      id: `category-${c.id}`,
      label: c.name,
      description: "",
      visual: getCategoryVisual(c.color, c.icon),
      units: categoryMap.get(c.id) ?? [],
    }));
    const bucketGroups: Group[] = BUCKETS.map((b) => ({
      id: b.id,
      label: b.label,
      description: b.description,
      visual: BUCKET_VISUALS[b.id],
      units: bucketMap.get(b.id) ?? [],
    }));
    // Category sections first (in displayOrder from the server), then legacy
    // buckets for unassigned templates. Drop any empty section.
    return [...categoryGroups, ...bucketGroups].filter((g) => g.units.length > 0);
  }, [browseAdUnits, searchTerm, briteCoSlugSet, dateFilter, newestFirst, templateCategories]);

  const totalVisible = useMemo(
    () => grouped.reduce((sum, g) => sum + g.units.length, 0) + briteCoTemplates.length,
    [grouped, briteCoTemplates],
  );

  // ── Server-driven generation lifecycle ──────────────────────────────────
  // The worker runs server-side now. We:
  //   1. Hydrate `activeGenerationId` from `?runId=` (notification deep link)
  //      or from /active (latest non-terminal run for this user+brand).
  //   2. Poll /api/ad-generations/:id every 3s while the parent is non-terminal.
  //   3. Derive `jobs[]` from the polled ads + adUnits map.

  // Hydration from URL: `?runId=...` always wins (deep link from notifs).
  useEffect(() => {
    if (hydratedFromUrlRef.current) return;
    const params = new URLSearchParams(search);
    const runId = params.get("runId");
    if (runId) {
      hydratedFromUrlRef.current = true;
      setActiveGenerationId(runId);
    }
  }, [search]);

  // Hydration from server: pick up the latest non-terminal run on mount.
  // Skipped if the URL already provided one.
  const { data: activeRun } = useQuery<{
    generation: ServerGeneration | null;
    ads: ServerGeneratedAd[];
  }>({
    queryKey: [`/api/brands/${brandId}/ad-generations/active`, "standard"],
    queryFn: () =>
      fetch(`/api/brands/${brandId}/ad-generations/active?variant=standard`, { credentials: "include" }).then((r) => r.json()),
    enabled: !!brandId && !hydratedFromUrlRef.current && !activeGenerationId,
    staleTime: 0,
  });
  useEffect(() => {
    if (activeGenerationId) return;
    if (activeRun?.generation && !hydratedFromUrlRef.current) {
      setActiveGenerationId(activeRun.generation.id);
    }
  }, [activeRun, activeGenerationId]);

  // Live poll the active generation. Stop polling once the parent reaches a
  // terminal state — the last successful response stays cached so the UI can
  // keep rendering completed tiles.
  const { data: liveGen } = useQuery<{
    generation: ServerGeneration;
    ads: ServerGeneratedAd[];
  }>({
    queryKey: [`/api/ad-generations`, activeGenerationId],
    queryFn: async () => {
      const res = await fetch(`/api/ad-generations/${activeGenerationId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load generation (${res.status})`);
      return res.json();
    },
    enabled: !!activeGenerationId,
    refetchInterval: (q) => {
      const status = (q.state.data as any)?.generation?.status as ServerGenerationStatus | undefined;
      if (!status) return 3000;
      return TERMINAL_GEN_STATUSES.includes(status) ? false : 3000;
    },
    refetchOnWindowFocus: true,
  });

  // Hydrate the input pickers when we resume a run from the server (so the
  // header reflects the persona/product/angle that produced these tiles).
  // Task #744 — clear a stale variant pick when the product changes.
  useEffect(() => {
    setSelectedVariantName(null);
  }, [productId]);

  useEffect(() => {
    if (!liveGen?.generation) return;
    const g = liveGen.generation;
    // Switching queue/history runs replaces this attachment as one tuple.
    // Filling only blank controls leaked the previous run into the next submit.
    const attachment = adsExpressAttachmentFormState(g);
    preserveHydratedAttachment(g.id, attachment);
    setPersonaId(attachment.personaId);
    setPromotedItemType(attachment.promotedItemType);
    setProductId(attachment.productId);
    setOfferId(attachment.offerId);
    setAngleId(attachment.angleId);
    setProjectId(g.projectId ?? "");
    setSalesDiscountId(g.salesDiscountId ?? "");
    if (typeof (g as any).includeLogo === "boolean") setIncludeLogo((g as any).includeLogo);
    // Aspect-ratio selection is multi-select and persisted in localStorage;
    // it is intentionally NOT re-hydrated from the run (which may have fanned
    // out across several ratios). Each tile reflects its own ratio instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveGen?.generation?.id]);

  const isRunning = useMemo(() => {
    const s = liveGen?.generation?.status;
    return s === "queued" || s === "running";
  }, [liveGen?.generation?.status]);

  // Task #2117 — with the run-queue flag ON the form is NEVER locked by a
  // running run (each submission's options are snapshotted server-side at
  // submit time, so later edits can't affect queued runs). Flag OFF keeps
  // the original `disabled={formLocked}` behavior on every control below.
  const formLocked = isRunning && !runQueueEnabled;

  // The user's non-terminal runs, oldest first (queue order). Polling this
  // also self-heals the server-side queue after a restart.
  const { data: runQueueData } = useAdsExpressRunQueue(brandId, "standard", runQueueEnabled);
  const queuedRuns = runQueueData?.runs ?? [];
  // If nothing is selected yet (no ?runId=, /active still pending or empty),
  // prefer the head of the queue — the run that's actually running.
  useEffect(() => {
    if (!runQueueEnabled || activeGenerationId || hydratedFromUrlRef.current) return;
    if (queuedRuns.length > 0) setActiveGenerationId(queuedRuns[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQueueEnabled, activeGenerationId, queuedRuns.length]);

  // Build a signature for the current header settings + the last run's
  // server-recorded settings. We use these to detect when the user has
  // tried to "Generate" with the exact same inputs as the last run — in
  // which case we keep the button disabled until they actually change
  // something at the top, so they don't burn credits re-running an
  // identical batch by accident. (Per-tile Regen is the right tool for
  // re-rolling individual ads.)
  const currentSignature = useMemo(() => {
    if (!canGenerate) return null;
    const ids = Array.from(selectedTemplateIds).sort().join(",");
    return [
      personaId,
      promotedItemType,
      productId,
      offerId,
      // In custom mode the angle is generated on submit, so key the signature
      // off the concept text instead of the (empty) angleId.
      angleMode === "custom" ? `concept:${concept.trim()}` : angleId,
      projectId || "",
      [...aspectRatios].sort().join(","),
      salesDiscountId || "",
      ids,
      includeLogo ? "logo" : "nologo",
      // Task #744 — mirror the POST (offer mode sends no variant) so the
      // "same as last run" guard matches the snapshot on the last run.
      (promotedItemType === "offer" ? null : selectedVariantName) || "novariant",
    ].join("|");
  }, [canGenerate, personaId, promotedItemType, productId, offerId, angleMode, concept, angleId, projectId, aspectRatios, salesDiscountId, selectedTemplateIds, includeLogo, selectedVariantName]);

  const lastRunSignature = useMemo(() => {
    const g = liveGen?.generation;
    if (!g) return null;
    const ids = (g.selectedAdUnitIds ?? [])
      .filter((x): x is string => typeof x === "string")
      .slice()
      .sort()
      .join(",");
    // Derive the distinct set of ratios the last run actually fanned out
    // across (from the child ads), falling back to the parent's single
    // ratio, so the "same as last run" guard works with multi-select.
    const runRatios = Array.from(
      new Set(
        (liveGen?.ads ?? [])
          .map((a) => a.aspectRatio)
          .filter((x): x is string => typeof x === "string"),
      ),
    );
    const ratioSig = (runRatios.length > 0 ? runRatios : [g.aspectRatio ?? "4:5"])
      .sort()
      .join(",");
    return [
      g.personaId ?? "",
      (g as any).promotedItemType ?? "product",
      g.productId ?? "",
      (g as any).offerId ?? "",
      g.narrativeAngleId ?? "",
      g.projectId ?? "",
      ratioSig,
      g.salesDiscountId ?? "",
      ids,
      (g as any).includeLogo !== false ? "logo" : "nologo",
      // Task #744 — keep signature parity with currentSignature; the last run's
      // variant lives on the snapshot (offer mode never carries one → "novariant").
      (g as any).selectedVariantSnapshot?.name || "novariant",
    ].join("|");
  }, [liveGen?.generation, liveGen?.ads]);

  const sameAsLastRun =
    currentSignature !== null &&
    lastRunSignature !== null &&
    currentSignature === lastRunSignature;

  // Map server ad rows → JobTile-friendly Job objects, layering local
  // overrides on top (Save spinner, inpainted images).
  const jobs: Job[] = useMemo(() => {
    if (!liveGen?.ads) return [];
    // Task #1654 — resolve against the display union so a master's
    // cross-brand picks show their real template names (not "Ad unit").
    const adUnitById = new Map(templatesForDisplay.map((u) => [u.id, u]));
    // Preserve the original server order: each tile stays in the same
    // grid slot for the entire run, regardless of whether it's still
    // rendering, completed, failed, or cancelled. (Previously we hoisted
    // newly-completed tiles to the front so users didn't have to scroll,
    // but that made the grid jump around mid-run.)
    return liveGen.ads
      // Task #1705 — manual uploads have no template; include them explicitly.
      .filter((a) => !!a.adUnitTypeId || a.source === "manual_upload")
      .map((a) => {
        const u = a.adUnitTypeId ? adUnitById.get(a.adUnitTypeId) : undefined;
        const o = localOverrides[a.id] ?? {};
        const baseStatus: JobStatus =
          a.status === "needs_review" ? "failed"
          : a.status === "ready" ? "completed"
          : a.status === "rendering" ? "rendering"
          : a.status === "queued" ? "queued"
          : a.status === "failed" ? "failed"
          : a.status === "cancelled" ? "cancelled"
          : "queued";
        // `pending` (not yet claimed by the worker) shows the spinner
        // while the run is active so the user sees activity. Real
        // `queued` rows (sim done, waiting for a Fal slot) keep the
        // "Queued — starting shortly" label so the user understands
        // why it isn't rendering yet.
        const status: JobStatus =
          baseStatus === "queued" && a.status === "pending" && isRunning
            ? "rendering"
            : baseStatus;
        return {
          // Manual uploads have no ad unit; fall back to the row id so
          // tile keys / testids stay unique.
          adUnitId: a.adUnitTypeId ?? a.id,
          isManual: a.source === "manual_upload",
          manualTags:
            a.source === "manual_upload"
              ? [a.personaNameSnapshot, a.productNameSnapshot ?? a.offerNameSnapshot, a.creativeAngleNameSnapshot].filter(
                  (t): t is string => !!t?.trim(),
                )
              : undefined,
          // Task #1572 — prefer the auto-generated convention name; legacy
          // rows (null snapshot) keep the template name. customName is
          // layered on top downstream (`customName || adUnitName`).
          adUnitName:
            a.defaultNameSnapshot?.trim() ||
            u?.name ||
            (a.source === "manual_upload" ? "Uploaded image" : "Ad unit"),
          // Which template produced this ad — the name snapshot captured at
          // creation, falling back to a live lookup for legacy rows. Null on
          // manual uploads (no template).
          templateName:
            a.source === "manual_upload"
              ? null
              : a.adUnitNameSnapshot?.trim() || u?.name || null,
          customName: o.customName !== undefined ? o.customName : (a.customName ?? null),
          status,
          imageUrl: o.imageUrl ?? a.imageUrlSaved ?? a.imageUrlOriginal,
          error: a.errorMessage,
          generatedAdId: a.id,
          isSaved: o.isSaved ?? a.isSaved,
          saving: o.saving ?? false,
          aspectRatio: normalizeAspectRatio(a.aspectRatio ?? liveGen?.generation?.aspectRatio),
        };
      });
  }, [liveGen?.ads, templatesForDisplay, localOverrides, isRunning, activeGenerationId]);

  // Poll a (possibly already-running) inline image edit to completion IN THE
  // BACKGROUND, so closing the edit modal mid-edit keeps the tile's loading
  // overlay and auto-applies the result when it lands. Deduped per tile via
  // pollingTilesRef; bails without clearing the persisted job when the page
  // unmounts (so it re-attaches on return). Defers to the modal if it's open
  // again on this tile — the modal owns the review/accept flow there.
  const pollEditJob = useCallback(
    async (
      jobId: string,
      tile: { id: string; kind: "job" | "reframe"; generatedAdId?: string; imageUrl: string; name: string },
    ) => {
      if (pollingTilesRef.current.has(tile.id)) return;
      pollingTilesRef.current.add(tile.id);
      markTileEditing(tile.id, true);

      let outcome: { type: "ready"; url: string } | { type: "failed"; message: string };
      while (true) {
        if (!mountedRef.current) {
          pollingTilesRef.current.delete(tile.id);
          return; // page gone — leave pending for re-attach
        }
        try {
          const pollRes = await readImageEditStatus(jobId);
          if (!mountedRef.current) { pollingTilesRef.current.delete(tile.id); return; }
          if (pollRes.ok) {
            const status = (await pollRes.json()) as {
              status: "pending" | "ready" | "failed";
              image_url?: string;
              message?: string;
              phase?: string;
            };
            if (!mountedRef.current) { pollingTilesRef.current.delete(tile.id); return; }
            if (status.status === "pending") {
              setTileEditPhases((prev) =>
                prev[tile.id] === (status.phase ?? null) ? prev : { ...prev, [tile.id]: status.phase ?? null },
              );
            }
            if (status.status === "ready" && status.image_url) {
              outcome = { type: "ready", url: status.image_url };
              break;
            }
            if (status.status === "failed") {
              outcome = { type: "failed", message: status.message || "Edit failed" };
              break;
            }
          }
        } catch {
          if (mountedRef.current) setTileEditPhases(prev => ({ ...prev, [tile.id]: "checking" }));
        }
        await waitForImageEditCheck(jobId);
      }

      if (!mountedRef.current) { pollingTilesRef.current.delete(tile.id); return; }

      // If the edit modal is open again on this tile, defer to its own poller
      // (it surfaces the result for review/accept and clears the pending job).
      const modalOpen =
        (tile.kind === "job" &&
          !!editingJobRef.current &&
          (editingJobRef.current.generatedAdId ?? editingJobRef.current.adUnitId) === tile.id) ||
        (tile.kind === "reframe" && editingReframeIdRef.current === tile.id);

      if (outcome.type === "ready") {
        const url = outcome.url;
        markTileEditing(tile.id, false);
        const tracked = findPendingImageEdit(tile.imageUrl);
        if (modalOpen || !tracked || tracked.jobId !== jobId || tracked.delivery === "review") {
          pollingTilesRef.current.delete(tile.id);
          return;
        }
        updatePendingImageEdit(jobId, { delivery: "background", resultImageUrl: url });
        if (tile.kind === "job" && tile.generatedAdId) {
          const genAdId = tile.generatedAdId;
          try {
            await persistPendingImageEdit(jobId, () => persistAcceptedEdit(genAdId, url));
            if (mountedRef.current) setLocalOverrides((prev) => ({ ...prev, [genAdId]: { ...(prev[genAdId] ?? {}), imageUrl: url } }));
          } catch (error: any) {
            updatePendingImageEdit(jobId, { delivery: "review", error: error?.message || "Couldn't persist edit" });
            if (mountedRef.current) setTileEditErrors(prev => ({ ...prev, [tile.id]: error?.message || "Couldn't persist edit" }));
            pollingTilesRef.current.delete(tile.id);
            return;
          }
        } else if (tile.kind === "reframe") {
          setReframeTiles((prev) =>
            prev.map((t) => (t.id === tile.id ? { ...t, imageUrl: url, status: "ready" as const } : t)),
          );
          clearPendingImageEdit(jobId);
        }
        toast({ title: "Edit applied", description: tile.name });
      } else if (outcome.type === "failed") {
        const message = outcome.message;
        setTileEditErrors((prev) => ({ ...prev, [tile.id]: message }));
        markTileEditing(tile.id, false);
        if (modalOpen) { pollingTilesRef.current.delete(tile.id); return; }
        updatePendingImageEdit(jobId, { delivery: "review", error: message });
        toast({ title: "Edit failed", description: message, variant: "destructive" });
      }
      pollingTilesRef.current.delete(tile.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markTileEditing, activeGenerationId, persistAcceptedEdit],
  );

  // Re-attach a background poller for any tile whose inline edit is still in
  // flight (edit modal closed mid-edit, or the page was refreshed / navigated
  // back). Restores the "Regenerating…" overlay and auto-applies the result.
  // Skips tiles whose edit modal is currently open (the modal owns those).
  useEffect(() => {
    for (const j of jobs) {
      const id = j.generatedAdId ?? j.adUnitId;
      if (!j.imageUrl) continue;
      if (editingJob && (editingJob.generatedAdId ?? editingJob.adUnitId) === id) continue;
      const pending = findPendingImageEdit(j.imageUrl);
      if (pending && !pending.target && pending.delivery !== "review" && !pending.error && !pollingTilesRef.current.has(id)) {
        void pollEditJob(pending.jobId, {
          id,
          kind: "job",
          generatedAdId: j.generatedAdId,
          imageUrl: j.imageUrl,
          name: j.adUnitName,
        });
      }
    }
    for (const t of reframeTiles) {
      if (!t.imageUrl) continue;
      if (editingReframeId === t.id) continue;
      const pending = findPendingImageEdit(t.imageUrl);
      if (pending && !pending.target && pending.delivery !== "review" && !pending.error && !pollingTilesRef.current.has(t.id)) {
        void pollEditJob(pending.jobId, {
          id: t.id,
          kind: "reframe",
          imageUrl: t.imageUrl,
          name: t.sourceAdName,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, reframeTiles, editingJob, editingReframeId, pollEditJob, imageEditsRevision]);

  // ── Advanced settings: visual reference upload handlers. Uploads go through
  // the shared /api/upload endpoint (returns { url }), same as the product
  // media uploader. We cap at MAX_AD_REFERENCES and validate type/size client
  // side; the server re-normalizes the list on submit.
  const handleReferenceFile = async (file: File, purpose: "auto" | "template_photo" = "auto") => {
    if (!templatePhotoFeatures.enabled && references.length >= MAX_AD_REFERENCES) {
      toast({
        title: "Reference limit reached",
        description: `You can attach up to ${MAX_AD_REFERENCES} reference images.`,
        variant: "destructive",
      });
      return;
    }
    const fileError = adsExpressReferenceUploadError(file);
    const capacityError = referenceSelectionError([...references, { url: "pending-upload", note: "", purpose }]);
    if (fileError || capacityError) {
      toast({
        title: fileError ? "Invalid reference image" : "Reference limit reached",
        description: fileError || capacityError,
        variant: "destructive",
      });
      return;
    }
    setUploadingRef(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(ADS_EXPRESS_REFERENCE_UPLOAD_PATH, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) {
        const error = await res.json().catch(() => null);
        throw new Error(error?.message || `Upload failed (${res.status})`);
      }
      const data = (await res.json()) as { url?: string };
      if (!data.url) throw new Error("Upload returned no URL");
      setReferences((prev) => {
        const candidate = [...prev, { url: data.url!, note: "", purpose }];
        const capacityError = referenceSelectionError(candidate);
        if (capacityError) {
          toast({ title: "Photo limit reached", description: capacityError, variant: "destructive" });
          return prev;
        }
        return [
          ...prev,
          {
            url: data.url!,
            note: "",
            purpose,
            ...(templatePhotoFeatures.enabled ? { source: "upload" as const } : {}),
          },
        ];
      });
    } catch (e: any) {
      toast({
        title: "Upload failed",
        description: e?.message ?? "Could not upload the reference image.",
        variant: "destructive",
      });
    } finally {
      setUploadingRef(false);
    }
  };

  const handleReferenceInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-selecting the same file
    const batchPurpose = templatePhotoFeatures.enabled
      ? templatePhotoAdditionPurpose(isOfferMode, files.length)
      : "auto";
    const batchError = referenceSelectionError([...references,
      ...files.map((_, i) => ({ url: `pending-upload-${i}`, note: "", purpose: batchPurpose }))]);
    if (batchError) {
      toast({ title: "Reference capacity exceeded", description: batchError, variant: "destructive" });
      return;
    }
    for (const [index, file] of files.entries()) {
      // eslint-disable-next-line no-await-in-loop
      await handleReferenceFile(file, batchPurpose);
    }
  };

  const removeReference = (idx: number) => {
    setReferences((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateReferenceNote = (idx: number, note: string) => {
    setReferences((prev) => prev.map((r, i) => (i === idx ? { ...r, note } : r)));
  };

  const updateReferencePurpose = (
    idx: number,
    purpose: "auto" | "environment" | "product" | "product_environment" | "person_identity" | "template_photo",
  ) => {
    setReferences((prev) => {
      const next = prev.map((r, i) =>
        i === idx
          ? {
              ...r,
              purpose,
              identityConsent:
                purpose === "person_identity" ? r.identityConsent === true : undefined,
            }
          : r,
      );
      const error = adReferenceCapacityError(next);
      if (error) {
        toast({ title: "Photo limit reached", description: error, variant: "destructive" });
        return prev;
      }
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    if (!brandId || !persona) return;
    const usingCustom = angleMode === "custom";
    if (!usingCustom && !angle) return;
    if (usingCustom && !concept.trim()) return;
    if (isOfferMode ? !offer : !product) return;

    const identityRefs = references.filter((r) => r.purpose === "person_identity");
    if (identityRefs.length > 0 && !personIdentityReferencesEnabled) {
      toast({
        title: "Person / identity references are unavailable",
        description: "Ask a brand administrator to enable this feature, or choose another reference purpose.",
        variant: "destructive",
      });
      return;
    }
    if (identityRefs.some((r) => r.identityConsent !== true)) {
      toast({
        title: "Likeness permission required",
        description: "Confirm you have permission to use each referenced person's likeness.",
        variant: "destructive",
      });
      return;
    }
    const selectedMeme = templatesForDisplay.find(
      (unit) => selectedTemplateIds.has(unit.id) && unit.isMeme === true,
    );
    if (identityRefs.length > 0 && selectedMeme) {
      toast({
        title: "Person / identity is not supported for meme templates",
        description: "Remove the meme template or choose Product / Environment / setting guidance.",
        variant: "destructive",
      });
      return;
    }

    // Safety net: never start a run that includes an ad unit without a
    // usable example image — the render would hard-fail server-side.
    // Task #1654 — checked over the display union so cross-brand picks get
    // the same guard (for current-brand rows the two predicates describe
    // the same set, so gated-off behavior is unchanged).
    const blocked = templatesForDisplay.filter(
      (u) =>
        selectedTemplateIds.has(u.id) &&
        (ineligibleTemplateIds.has(u.id) || !hasUsableExampleImage(u)),
    );
    if (blocked.length > 0) {
      const names = blocked.map((u) => shortNameOf(u.slug, u.name)).join(", ");
      toast({
        title: "Add an example image first",
        description: `These templates have no example image and can't be generated: ${names}. Add one in the ad-unit settings.`,
        variant: "destructive",
      });
      return;
    }

    const jobIds = Array.from(selectedTemplateIds);

    setSubmitting(true);
    try {
      const generationPayload = buildAdGenerationPayload({
        projectId,
        personaId: persona.id,
        promotedItemType,
        isOfferMode,
        productId: isOfferMode ? null : product!.id,
        offerId: isOfferMode ? offer!.id : null,
        usingCustom,
        angleId: usingCustom ? null : angle!.id,
        concept,
        adUnitTypeIds: jobIds,
        aspectRatios,
        salesDiscountId,
        includeLogo,
        references,
        additionalContext,
        selectedVariantName: isOfferMode ? null : selectedVariantName,
        productImageUrls: productRefLibraryEnabled ? selectedLibraryImages : null,
        adFontFamily: googleFontAdsEnabled ? adFontFamily : null,
        imageEngineChoice:
          adsExpressImageEngineChoiceEnabled && imageEngineChoice
            ? imageEngineChoice
            : null,
        selectedPersonaCharacterId:
          characterPickerAvailable ? selectedPersonaCharacterId : null,
      });
      if (templatePhotoFeatures.enabled) {
        const sourceByUrl = new Map(references.map((reference) => [reference.url, reference.source]));
        (generationPayload.references as any[]).forEach((reference) => {
          const source = sourceByUrl.get(reference.url);
          if (source) reference.source = source;
        });
      }
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/ad-generations`,
        generationPayload,
      );
      saveLastAspectRatios(aspectRatios);
      const data = (await res.json()) as {
        generation: { id: string; status?: string; narrativeAngleId?: string | null };
      };
      const newId = data.generation.id;
      // Task #2117 — a submit that landed behind an existing run comes back
      // QUEUED. Refresh the queue panel and tell the user; if they're already
      // watching a run, stay on it (the queued run is a click away).
      const wasQueued = data.generation.status === "queued";
      if (wasQueued) {
        invalidateAdsExpressRunQueue(brandId, "standard");
        toast({
          title: "Run queued",
          description: "It will start automatically when the earlier run finishes.",
        });
      }

      // When the server transformed a custom concept into a saved angle,
      // adopt it locally and flip back to saved mode so the new angle is
      // visible/selectable as a normal saved angle going forward. The angles
      // query uses staleTime: Infinity, so we must refetch before adopting the
      // id — otherwise the auto-clear effect wipes it (it's not yet in the
      // stale `availableAngles`). Awaiting the invalidation guarantees the
      // cache contains the new angle by the time we set angleId.
      if (usingCustom && data.generation.narrativeAngleId) {
        await queryClient.invalidateQueries({
          queryKey: [`/api/brands/${brandId}/narrative-angles`],
        });
        setAngleId(data.generation.narrativeAngleId);
        setAngleMode("saved");
        setConcept("");
      }

      // The server kicks off the worker as part of the create call, and
      // GET /active + GET /:id self-heal by reattaching the worker if it
      // ever got dropped. So a single round-trip is enough; even if the
      // user navigates away right now, the run continues server-side.

      // Reset local state and pivot to the new run — unless it was queued
      // behind a run the user is already watching.
      if (!wasQueued || !activeGenerationId) {
        setLocalOverrides({});
        setActiveGenerationId(newId);
      }
      hydratedFromUrlRef.current = true; // don't override with /active
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({
        title: "Couldn't start generation",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (generatedAdId: string) => {
    const job = jobs.find((j) => j.generatedAdId === generatedAdId);
    if (!job || !job.generatedAdId || job.isSaved || job.saving) return;
    if (job.status !== "completed" || !job.imageUrl) return;
    setOverride(job.generatedAdId, { saving: true });
    try {
      // If the tile was edited in place, the edited image lives in the local
      // override; send it so the server persists the on-screen version rather
      // than the original render. When unedited we send an empty body and the
      // server falls back to imageUrlOriginal.
      const editedImageUrl = localOverrides[job.generatedAdId]?.imageUrl;
      await apiRequest(
        "POST",
        `/api/generated-ads/${job.generatedAdId}/save`,
        editedImageUrl ? { imageUrl: editedImageUrl } : {},
      );
      setOverride(job.generatedAdId, { isSaved: true, saving: false });
      // Task #2040 — an ad edited before saving becomes undoable at save.
      invalidateUndoableAds(brandId);
      invalidateGeneratedAdListings();
      toast({ title: "Saved to library", description: job.adUnitName });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setOverride(job.generatedAdId, { saving: false });
      toast({ title: "Save failed", description: msg, variant: "destructive" });
    }
  };

  // Rename a finished tile. Optimistic local override so the title (and
  // download filenames) update immediately even after polling has stopped;
  // the PATCH persists it so the name carries into My Ads on save.
  const handleRename = async (generatedAdId: string, name: string | null) => {
    const prev = jobs.find((j) => j.generatedAdId === generatedAdId)?.customName ?? null;
    setOverride(generatedAdId, { customName: name });
    setLightboxJob((lb) => (lb?.generatedAdId === generatedAdId ? { ...lb, customName: name } : lb));
    try {
      await apiRequest("PATCH", `/api/generated-ads/${generatedAdId}`, { customName: name });
      queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
    } catch (e: unknown) {
      setOverride(generatedAdId, { customName: prev });
      setLightboxJob((lb) => (lb?.generatedAdId === generatedAdId ? { ...lb, customName: prev } : lb));
      toast({ title: "Couldn't rename", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
      throw e;
    }
  };

  const [savingAll, setSavingAll] = useState(false);
  const handleSaveAll = async () => {
    if (savingAll) return;
    const pending = jobs.filter(
      (j) => j.status === "completed" && !!j.imageUrl && !!j.generatedAdId && !j.isSaved && !j.saving,
    );
    if (pending.length === 0) return;
    setSavingAll(true);
    // Optimistically flip every tile into the saving state up-front so
    // the user sees per-tile spinners immediately, then fire requests
    // in parallel. Each per-tile result clears its own override.
    for (const j of pending) {
      if (j.generatedAdId) setOverride(j.generatedAdId, { saving: true });
    }
    const results = await Promise.allSettled(
      pending.map(async (j) => {
        // Persist the on-screen image for tiles edited in place; unedited
        // tiles send an empty body and the server falls back to the original.
        const editedImageUrl = localOverrides[j.generatedAdId!]?.imageUrl;
        await apiRequest(
          "POST",
          `/api/generated-ads/${j.generatedAdId!}/save`,
          editedImageUrl ? { imageUrl: editedImageUrl } : {},
        );
        setOverride(j.generatedAdId!, { isSaved: true, saving: false });
      }),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    // Task #2040 — ads edited before saving become undoable at save.
    invalidateUndoableAds(brandId);
    if (failed < pending.length) {
      invalidateGeneratedAdListings();
    }
    // Clear the saving flag on any rows that errored out so the tile
    // returns to its idle "Save" state instead of getting stuck.
    pending.forEach((j, i) => {
      if (results[i].status === "rejected" && j.generatedAdId) {
        setOverride(j.generatedAdId, { saving: false });
      }
    });
    setSavingAll(false);
    if (failed === 0) {
      toast({ title: "Saved to library", description: `${pending.length} ad${pending.length === 1 ? "" : "s"} saved.` });
    } else {
      toast({
        title: failed === pending.length ? "Save all failed" : "Some ads didn't save",
        description: `${pending.length - failed} of ${pending.length} saved.`,
        variant: "destructive",
      });
    }
  };

  const handleCancel = async () => {
    if (!brandId || !activeGenerationId || cancelling) return;
    setCancelling(true);
    try {
      await apiRequest("POST", `/api/brands/${brandId}/ad-generations/${activeGenerationId}/cancel`, {});
      toast({
        title: "Cancelling",
        description: "In-flight renders will finish; no new jobs will start.",
      });
      // Force an immediate refetch so the UI flips quickly.
      queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Cancel failed", description: msg, variant: "destructive" });
    } finally {
      setCancelling(false);
    }
  };

  // Retry a single tile: PATCH back to pending and re-start the worker.
  // The worker is idempotent and will pick up the newly-pending child.
  const handleRetry = async (generatedAdId: string) => {
    const job = jobs.find((j) => j.generatedAdId === generatedAdId);
    if (!job?.generatedAdId || !brandId || !activeGenerationId) return;
    try {
      // Note: we intentionally do NOT clear imageUrlOriginal here. Keeping
      // the previous image on the row lets the tile render the old image
      // (with a spinner overlay) while the worker re-renders, so the tile
      // stays the same visual size and doesn't move within the grid. The
      // worker overwrites imageUrlOriginal when the new render lands.
      await apiRequest("PATCH", `/api/generated-ads/${job.generatedAdId}`, {
        status: "pending",
        errorMessage: null,
      });
      await apiRequest("POST", `/api/brands/${brandId}/ad-generations/${activeGenerationId}/start`, {});
      queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Retry failed", description: msg, variant: "destructive" });
    }
  };

  // Task #2439 — master-only "Regenerate with fresh prompt". Unlike Regen
  // (same stored prompt, new roll), this asks the server to clear the ad's
  // prompt + Fal request id so the worker re-runs the Sim prompt workflow
  // with the run's original snapshotted inputs, then renders anew. The
  // server enforces the master gate; the button is simply hidden otherwise.
  const handleFreshRegen = async (generatedAdId: string) => {
    try {
      await apiRequest("POST", `/api/generated-ads/${generatedAdId}/fresh-prompt-regen`, {});
      queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
      toast({
        title: "Regenerating with a fresh prompt",
        description: "A new prompt is being written from this ad's original run inputs — the tile will update when the new render lands.",
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Fresh-prompt regenerate failed", description: msg, variant: "destructive" });
    }
  };

  // ── Reframe (inline): adds a new tile to the grid in `loading` state,
  // fires the preview-only /reframe endpoint, and updates the tile when
  // the image lands. The user can kick off more reframes in parallel
  // while one is rendering — each tile is independent.
  const updateReframeTile = (id: string, patch: Partial<ReframeTile>) => {
    setReframeTiles((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  // Task #2161 — "Copy as is" / "Copy & reframe" on saved tiles. Both
  // create an independent saved duplicate in My Ads (own R2 object); the
  // original tile/row stays exactly as it was.
  const [copyingAdIds, setCopyingAdIds] = useState<Set<string>>(new Set());
  const handleCopyAd = async (job: Job, ratio: AspectRatio | null) => {
    if (!job.generatedAdId) return;
    const adId = job.generatedAdId;
    setCopyingAdIds((prev) => new Set(prev).add(adId));
    try {
      if (ratio) {
        await copyAndReframeAd(adId, ratio);
        toast({
          title: `Copy saved at ${ratio}`,
          description: "The original is untouched — both sizes are now in My Ads.",
        });
      } else {
        await duplicateAdAsIs(adId);
        toast({
          title: "Copy saved to My Ads",
          description: "An independent duplicate was added to your library.",
        });
      }
      // Refresh the My Ads grids so the copy shows up immediately.
      invalidateGeneratedAdListings();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Copy failed", description: msg, variant: "destructive" });
    } finally {
      setCopyingAdIds((prev) => {
        const next = new Set(prev);
        next.delete(adId);
        return next;
      });
    }
  };

  const startReframe = async (job: Job, ratio: "1:1" | "4:5" | "9:16") => {
    if (!job.generatedAdId) return;
    const generatedAdId = job.generatedAdId;
    const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r_${Date.now()}_${Math.random()}`;
    setReframeTiles((prev) => [
      ...prev,
      {
        id,
        sourceAdUnitId: job.adUnitId,
        sourceGeneratedAdId: generatedAdId,
        sourceAdName: job.customName?.trim() || job.adUnitName,
        aspectRatio: ratio,
        status: "loading",
      },
    ]);
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${job.generatedAdId}/reframe`, { aspectRatio: ratio });
      const data = (await res.json()) as { imageUrl: string; aspectRatio: string; source: ReframeSource };
      if (!data?.imageUrl) throw new Error("No image returned from reframe");
      updateReframeTile(id, { imageUrl: data.imageUrl, source: data.source });
      await validateReframePreview(data.imageUrl, ratio);
      updateReframeTile(id, { status: "ready", imageUrl: data.imageUrl, source: data.source });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      updateReframeTile(id, { status: "error", errorMsg: msg });
    }
  };

  const saveReframeTile = async (tile: ReframeTile) => {
    if (reframeTileSavesInFlight.current.has(tile.id)) return;
    if (tile.status !== "ready" || !tile.imageUrl || !tile.source) return;
    if (!tile.source.adUnitId) {
      console.error("[reframe] Ads Express save blocked: response is missing its ad-unit reference", {
        generatedAdId: tile.sourceGeneratedAdId,
        brandId: tile.source.brandId,
        parentAdId: tile.sourceGeneratedAdId,
      });
      toast({
        title: "We couldn't save this version",
        description: "Please try again. If it keeps happening, contact support and we'll help.",
        variant: "destructive",
      });
      return;
    }
    reframeTileSavesInFlight.current.add(tile.id);
    updateReframeTile(tile.id, { status: "saving" });
    try {
      const response = await apiRequest("POST", `/api/brands/${tile.source.brandId}/library/save-generated-image`, {
        imageUrl: tile.imageUrl,
        parentAdId: tile.source.parentAdId,
        adUnitId: tile.source.adUnitId,
        personaId: tile.source.personaId,
        productId: tile.source.productId,
        narrativeAngleId: tile.source.narrativeAngleId,
        projectId: tile.source.projectId,
        prompt: tile.source.prompt,
        aspectRatio: tile.aspectRatio,
      });
      const saved = await response.json() as { ad?: { customName?: string | null; defaultNameSnapshot?: string | null } };
      updateReframeTile(tile.id, {
        status: "saved",
        sourceAdName: saved.ad?.customName?.trim() || saved.ad?.defaultNameSnapshot?.trim() || tile.sourceAdName,
      });
      invalidateGeneratedAdListings();
      toast({ title: "Saved to My Ads", description: `${tile.aspectRatio} reframe added to your library.` });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Save failed", description: msg, variant: "destructive" });
      updateReframeTile(tile.id, { status: "ready" });
    } finally {
      reframeTileSavesInFlight.current.delete(tile.id);
    }
  };

  const dismissReframeTile = (id: string) => {
    setReframeTiles((prev) => prev.filter((t) => t.id !== id));
  };

  const retryReframeTile = async (tile: ReframeTile) => {
    if (tile.status !== "error") return;
    const sourceJob = jobs.find((j) => j.generatedAdId === tile.sourceGeneratedAdId);
    if (!sourceJob?.generatedAdId) {
      toast({ title: "Can't retry", description: "Source ad is no longer available.", variant: "destructive" });
      return;
    }
    updateReframeTile(tile.id, { status: "loading", errorMsg: undefined });
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${sourceJob.generatedAdId}/reframe`, { aspectRatio: tile.aspectRatio });
      const data = (await res.json()) as { imageUrl: string; aspectRatio: string; source: ReframeSource };
      if (!data?.imageUrl) throw new Error("No image returned from reframe");
      updateReframeTile(tile.id, { imageUrl: data.imageUrl, source: data.source });
      await validateReframePreview(data.imageUrl, tile.aspectRatio);
      updateReframeTile(tile.id, { status: "ready", imageUrl: data.imageUrl, source: data.source });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      updateReframeTile(tile.id, { status: "error", errorMsg: msg });
    }
  };

  // When jobs first appear, scroll the results grid into view.
  const hadJobsRef = useRef(false);
  useEffect(() => {
    if (jobs.length > 0 && !hadJobsRef.current) {
      hadJobsRef.current = true;
      requestAnimationFrame(() => {
        jobsGridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } else if (jobs.length === 0) {
      hadJobsRef.current = false;
    }
  }, [jobs.length]);

  // Reset only the input pickers when brandId actually changes. The server
  // run keeps going on its own; the next mount on the new brand will hydrate
  // from /active for that brand. (Skip initial mount.)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      lastBrandIdRef.current = brandId;
      return;
    }
    if (lastBrandIdRef.current === brandId) return;
    lastBrandIdRef.current = brandId;
    console.log("[AdsExpress] brand changed, resetting pickers", brandId);
    setActiveGenerationId(null);
    setLocalOverrides({});
    setReframeTiles([]);
    hydratedFromUrlRef.current = false;
    setSelectedTemplateIds(new Set());
    setPersonaId("");
    setSelectedPersonaCharacterId("");
    setPromotedItemType("product");
    setProductId("");
    setOfferId("");
    setAngleId("");
    setAngleMode("saved");
    setConcept("");
    setImageEngineChoice("");
    setProjectId("");
    setSearchTerm("");
    setAddMode(false);
    setShowAllTemplates(false);
  }, [brandId]);

  const counts = useMemo(() => {
    return {
      total: jobs.length,
      done: jobs.filter((j) => j.status === "completed").length,
      failed: jobs.filter((j) => j.status === "failed").length,
      running: jobs.filter((j) => j.status === "rendering").length,
    };
  }, [jobs]);

  const progressPct = counts.total > 0
    ? Math.round(((counts.done + counts.failed) / counts.total) * 100)
    : 0;

  const downloadOne = async (url: string, name: string) => {
    const fileBase = sanitizeFilename(name);
    try {
      const proxyUrl = `/api/img-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(`${fileBase}.png`)}`;
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${fileBase}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    }
  };

  const downloadAll = async () => {
    const completed = jobs.filter((j) => j.status === "completed" && j.imageUrl);
    if (completed.length === 0) return;
    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      // Task #1572 / #2314 — same ad in multiple aspect ratios shares a base
      // name; the shared helper appends the ratio suffix only where needed so
      // filenames don't collide (and keeps the trailing date code + letter).
      const fileNames = buildZipFileNames(
        completed.map((j) => ({
          name: j.customName?.trim() || j.adUnitName,
          aspectRatio: j.aspectRatio,
        })),
      );
      await Promise.all(
        completed.map(async (j, idx) => {
          const fileName = fileNames[idx];
          const proxyUrl = `/api/img-download?url=${encodeURIComponent(j.imageUrl!)}&filename=${encodeURIComponent(fileName)}`;
          const res = await fetch(proxyUrl);
          if (!res.ok) throw new Error(`Download failed (${res.status})`);
          const blob = await res.blob();
          zip.file(fileName, blob);
        }),
      );
      const content = await zip.generateAsync({ type: "blob" });
      const objUrl = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `ads-express-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Zip failed", description: msg, variant: "destructive" });
    }
  };

  const isLoadingAny = loadingPersonas || loadingProducts || loadingOffers || loadingAngles || loadingAdUnits;

  const emptyResource =
    !isLoadingAny && (
      personas.length === 0 ? "personas" :
      (products.length === 0 && offers.length === 0) ? "products-or-offers" :
      angles.length === 0 ? "angles" :
      staticAdUnits.length === 0 ? "ad-units" :
      null
    );

  // Task #1654 — the existing "Static ad templates" section body, extracted
  // VERBATIM (internal indentation preserved) so it renders either bare —
  // cross-brand gate off ⇒ exactly today's unwrapped DOM — or under the
  // "This brand" tab when the master-only browser is on. Its query,
  // filtering, grouping, and rendering logic are untouched.
  const thisBrandTemplatesSection = (
    <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold tracking-tight">Static ad templates</h2>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="text-selected-count">
                  {selectedAccessibleTemplateCount} selected · {browseAdUnits.length} shown
                  {selectedAccessibleTemplateCount > selectedBrowseTemplateCount
                    ? ` · ${selectedAccessibleTemplateCount - selectedBrowseTemplateCount} selected hidden`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {crossBrandTabsEnabled && (
                  /* Task #1654 — master-only entry to the cross-brand
                     template browser; absent from the DOM when gated off. */
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 rounded-full text-xs shrink-0"
                    onClick={() => setShowOtherBrands(true)}
                    data-testid="button-browse-other-brands"
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    Other brands
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
                <div className="relative flex-1 sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search templates..."
                    className="pl-9 h-9 rounded-full text-xs"
                    disabled={formLocked}
                    data-testid="input-search-templates"
                  />
                </div>
                <Select
                  value={dateFilter}
                  onValueChange={(v) => setDateFilter(v as DateFilterId)}
                  disabled={formLocked}
                >
                  <SelectTrigger
                    className="h-9 rounded-full text-xs w-[140px] shrink-0"
                    data-testid="select-date-filter"
                  >
                    <CalendarClock className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FILTER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id} data-testid={`option-date-filter-${opt.id}`}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={newestFirst}
                        aria-label="Sort newest first"
                        onClick={() => setNewestFirst((v) => !v)}
                        disabled={formLocked}
                        className={[
                          "flex items-center gap-1.5 rounded-full px-3 h-9 border text-xs font-medium transition-colors shrink-0",
                          newestFirst
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border/60 text-muted-foreground hover:text-foreground",
                          formLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                        ].join(" ")}
                        data-testid="switch-newest-first"
                      >
                        <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                        Newest
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs max-w-xs">
                      Sort templates newest-first within each section
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                {showAllTemplates
                  ? "Showing every template available to this brand."
                  : hiddenUnapprovedCount > 0
                    ? `Showing brand-approved templates · ${hiddenUnapprovedCount} hidden`
                    : "All accessible templates are approved for this brand."}
              </p>
              {(hiddenUnapprovedCount > 0 || showAllTemplates) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full text-xs shrink-0"
                  onClick={() => setShowAllTemplates((value) => !value)}
                  disabled={formLocked}
                  data-testid="button-toggle-show-all-templates"
                >
                  {showAllTemplates ? "Show approved" : `Show all (${hiddenUnapprovedCount} hidden)`}
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground/70 font-semibold mr-1">
                Presets
              </span>
              {staticTemplateSets.length > 0 && (
                <Select
                  value={selectedTemplateSetId || undefined}
                  onValueChange={(setId) => {
                    setSelectedTemplateSetId(setId);
                    const set = staticTemplateSets.find((candidate) => candidate.id === setId);
                    if (!set) return;
                    // A set may contain a template that is no longer accessible
                    // to this brand. Ignore it rather than making the form
                    // invalid, while preserving the saved set itself.
                    const available = new Set(browseAdUnits.map((unit) => unit.id));
                    const ids = set.members
                      .map((member) => member.adUnitTypeId)
                      .filter((id) => available.has(id) && !ineligibleTemplateIds.has(id));
                    setSelectedTemplateIds((current) => addMode
                      ? new Set([...current, ...ids])
                      : new Set(ids));
                  }}
                >
                  <SelectTrigger
                    className="h-8 w-[190px] rounded-full text-xs"
                    disabled={formLocked}
                    data-testid="select-static-template-set"
                  >
                    <SelectValue placeholder="Saved template set" />
                  </SelectTrigger>
                  <SelectContent>
                    {staticTemplateSets.map((set) => (
                      <SelectItem key={set.id} value={set.id}>
                        {set.name} ({set.members.length})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {PRESETS.map((preset) => {
                const isActive = activePreset === preset.id;
                return (
                  <Button
                    key={preset.id}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => applyPreset(preset.id)}
                    disabled={formLocked}
                    className="rounded-full text-xs h-8"
                    data-testid={`button-preset-${preset.id}`}
                  >
                    {addMode ? <Plus className="h-3 w-3 mr-1" /> : isActive ? <Check className="h-3 w-3 mr-1" /> : null}
                    {preset.label}
                  </Button>
                );
              })}
              <div className="flex items-center gap-2 ml-auto">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={addMode}
                        aria-label="Toggle add mode"
                        onClick={() => setAddMode((v) => !v)}
                        disabled={formLocked}
                        className={[
                          "flex items-center gap-2 rounded-full px-3 h-8 border transition-colors",
                          addMode ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:text-foreground",
                          formLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                        ].join(" ")}
                        data-testid="switch-add-mode"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span className="text-xs font-medium">Add mode</span>
                        <span
                          aria-hidden="true"
                          className={[
                            "relative inline-block w-7 h-4 rounded-full transition-colors",
                            addMode ? "bg-primary" : "bg-muted-foreground/30",
                          ].join(" ")}
                        >
                          <span
                            className={[
                              "absolute top-0.5 h-3 w-3 rounded-full bg-background shadow transition-transform",
                              addMode ? "translate-x-3.5" : "translate-x-0.5",
                            ].join(" ")}
                          />
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs max-w-xs">
                      {addMode
                        ? "Presets ADD to your current selection"
                        : "Presets REPLACE your current selection"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSelectedTemplateIds(
                      new Set(
                        browseAdUnits
                          .filter((u) => !ineligibleTemplateIds.has(u.id))
                          .map((u) => u.id),
                      ),
                    )
                  }
                  disabled={
                    formLocked ||
                    eligibleTemplateCount === 0 ||
                    selectedBrowseTemplateCount === eligibleTemplateCount
                  }
                  data-testid="button-select-all"
                  className="rounded-full text-xs h-8"
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  disabled={formLocked || selectedTemplateIds.size === 0}
                  data-testid="button-clear-all"
                  className="rounded-full text-xs h-8"
                >
                  Clear
                </Button>
              </div>
            </div>

            <div
              className={[
                "rounded-md border px-3 py-2 text-[11px] flex items-center gap-2",
                addMode
                  ? "border-primary/40 bg-primary/5 text-primary"
                  : "border-border/50 bg-muted/30 text-muted-foreground",
              ].join(" ")}
              data-testid="banner-mode"
            >
              {addMode ? (
                <>
                  <Plus className="h-3 w-3" />
                  <span>
                    <strong>Add mode on.</strong> Clicking a preset adds its templates to your current selection.
                  </span>
                </>
              ) : (
                <>
                  <RotateCcw className="h-3 w-3" />
                  <span>
                    <strong>Replace mode.</strong> Clicking a preset replaces your current selection. Toggle Add mode to stack presets.
                  </span>
                </>
              )}
            </div>

            {totalVisible === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="text-no-results">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                {searchTerm.trim() ? (
                  <p className="text-sm">No templates match "{searchTerm}".</p>
                ) : dateFilter !== "all" ? (
                  <>
                    <p className="text-sm">
                      No templates added in the {DATE_FILTER_OPTIONS.find((o) => o.id === dateFilter)?.label.toLowerCase()}.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs mt-1 text-primary hover:text-primary"
                      onClick={() => setDateFilter("all")}
                      data-testid="button-clear-date-filter"
                    >
                      Clear date filter
                    </Button>
                  </>
                ) : (
                  <p className="text-sm">No templates available.</p>
                )}
              </div>
            ) : (
              <div className="space-y-7">
                {isBriteCoBrand && briteCoTemplates.length > 0 && (
                  <div data-testid="section-bucket-briteco">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 bg-violet-500/15 text-violet-600 dark:text-violet-400">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-bold tracking-tight" data-testid="text-bucket-label-briteco">
                              BriteCo Templates
                            </h3>
                            <Badge
                              variant="secondary"
                              className="h-5 px-1.5 text-[10px] font-bold tabular-nums"
                              data-testid="badge-bucket-count-briteco"
                            >
                              {briteCoTemplates.length}
                            </Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-1">
                            Curated templates only available to BriteCo.
                          </p>
                        </div>
                      </div>
                      <div className="hidden sm:block h-px flex-1 ml-3 opacity-30 bg-violet-500" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {briteCoTemplates.map((u) => (
                        <TemplateCard
                          key={u.id}
                          unit={u}
                          checked={selectedTemplateIds.has(u.id)}
                          disabled={formLocked}
                          ineligible={ineligibleTemplateIds.has(u.id)}
                          brandId={brandId}
                          onToggle={() => toggleTemplate(u.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {grouped.map(({ id, label, description, visual, units }) => {
                  const expanded = expandedBuckets.has(id);
                  const hasMore = units.length > BUCKET_COLLAPSED_COUNT;
                  const visibleUnits = expanded ? units : units.slice(0, BUCKET_COLLAPSED_COUNT);
                  const hiddenCount = units.length - BUCKET_COLLAPSED_COUNT;
                  return (
                    <div key={id} data-testid={`section-bucket-${id}`}>
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={["h-9 w-9 rounded-lg flex items-center justify-center shrink-0", visual.accent].join(" ")}>
                            {visual.icon}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-base font-bold tracking-tight" data-testid={`text-bucket-label-${id}`}>
                                {label}
                              </h3>
                              <Badge
                                variant="secondary"
                                className="h-5 px-1.5 text-[10px] font-bold tabular-nums"
                                data-testid={`badge-bucket-count-${id}`}
                              >
                                {units.length}
                              </Badge>
                            </div>
                            {description && (
                              <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-1">
                                {description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className={["hidden sm:block h-px flex-1 ml-3 opacity-30", visual.bar].join(" ")} />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {visibleUnits.map((u) => (
                          <TemplateCard
                            key={u.id}
                            unit={u}
                            checked={selectedTemplateIds.has(u.id)}
                            disabled={formLocked}
                            ineligible={ineligibleTemplateIds.has(u.id)}
                            brandId={brandId}
                            onToggle={() => toggleTemplate(u.id)}
                          />
                        ))}
                      </div>
                      {hasMore && (
                        <div className="mt-2.5 flex justify-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => toggleBucketExpanded(id)}
                            data-testid={`button-bucket-toggle-${id}`}
                          >
                            {expanded ? (
                              <>
                                View Less
                                <ChevronUp className="h-3.5 w-3.5" />
                              </>
                            ) : (
                              <>
                                View More (+{hiddenCount})
                                <ChevronDown className="h-3.5 w-3.5" />
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
    </>
  );

  // Task #1654 — master-only "All brands" tab, styled to MIRROR Tab 1: the
  // same header/toolbar look (count text, search, date filter, newest
  // toggle) plus a multi-select brand dropdown (default: every other
  // brand), and bucket-style sections grouped BY BRAND. Global templates
  // stay in Tab 1 only. Cards reuse TemplateCard and feed the SAME
  // selectedTemplateIds/toggle flow as Tab 1 — one shared selection.
  // brandId is passed empty on purpose: it suppresses TemplateCard's admin
  // "Add example image" edit link, so there is no edit affordance for
  // templates the current brand doesn't own.
  const foreignBrandFilterLabel =
    foreignBrandFilter === null
      ? "All brands"
      : foreignBrandFilter.size === 1
        ? otherBrands.find((b) => foreignBrandFilter.has(b.id))?.name ?? "1 brand"
        : `${foreignBrandFilter.size} brands`;
  const loadingForeignData = loadingAllBrands || loadingAllAdminUnits;
  const allBrandsTemplatesSection = !crossBrandTabsEnabled ? null : (
    <div className="space-y-4" data-testid="section-all-brands">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold tracking-tight">Static ad templates</h2>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="text-foreign-selected-count">
            {foreignSelectedCount} of {foreignPool.length} selected
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 rounded-full text-xs shrink-0"
            onClick={() => setShowOtherBrands(false)}
            data-testid="button-back-to-this-brand"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to this brand
          </Button>
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="search"
              value={foreignSearchTerm}
              onChange={(e) => setForeignSearchTerm(e.target.value)}
              placeholder="Search templates..."
              className="pl-9 h-9 rounded-full text-xs"
              disabled={formLocked}
              data-testid="input-search-foreign-templates"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={formLocked}
                className={[
                  "flex items-center gap-1.5 rounded-full px-3 h-9 border border-border/60 text-xs font-medium transition-colors shrink-0",
                  formLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-primary/60 hover:bg-primary/5",
                ].join(" ")}
                data-testid="select-foreign-brands"
              >
                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate max-w-[130px]">{foreignBrandFilterLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
              <DropdownMenuCheckboxItem
                checked={foreignBrandFilter === null}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => setForeignBrandFilter(null)}
                className="font-medium"
                data-testid="option-foreign-brand-all"
              >
                All brands
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              {otherBrands.map((b) => (
                <DropdownMenuCheckboxItem
                  key={b.id}
                  checked={foreignBrandFilter === null || foreignBrandFilter.has(b.id)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={() => toggleForeignBrandChecked(b.id)}
                  data-testid={`option-foreign-brand-${b.id}`}
                >
                  <span className="truncate">{b.name}</span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Select
            value={foreignDateFilter}
            onValueChange={(v) => setForeignDateFilter(v as DateFilterId)}
            disabled={formLocked}
          >
            <SelectTrigger
              className="h-9 rounded-full text-xs w-[140px] shrink-0"
              data-testid="select-foreign-date-filter"
            >
              <CalendarClock className="h-3.5 w-3.5 mr-1.5 text-muted-foreground shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} data-testid={`option-foreign-date-filter-${opt.id}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  role="switch"
                  aria-checked={foreignNewestFirst}
                  aria-label="Sort newest first"
                  onClick={() => setForeignNewestFirst((v) => !v)}
                  disabled={formLocked}
                  className={[
                    "flex items-center gap-1.5 rounded-full px-3 h-9 border text-xs font-medium transition-colors shrink-0",
                    foreignNewestFirst
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/60 text-muted-foreground hover:text-foreground",
                    formLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                  ].join(" ")}
                  data-testid="switch-foreign-newest-first"
                >
                  <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                  Newest
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs max-w-xs">
                Sort templates newest-first within each brand
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Templates owned by other brands. Selected ones render ads for{" "}
        <span className="font-medium text-foreground">{brand?.name ?? "the current brand"}</span>{" "}
        using the borrowed template as the visual reference — they count toward the same run as
        "This brand" picks.
      </p>

      {loadingForeignData ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, idx) => (
            <Skeleton key={idx} className="aspect-square rounded-lg" />
          ))}
        </div>
      ) : otherBrands.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="text-no-other-brands">
          No other brands to browse.
        </p>
      ) : foreignPool.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-foreign-no-templates">
          <Megaphone className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Other brands have no static ad templates of their own.</p>
        </div>
      ) : foreignVisibleCount === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-foreign-no-results">
          <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
          {foreignSearchTerm.trim() ? (
            <p className="text-sm">No templates match "{foreignSearchTerm}".</p>
          ) : foreignBrandFilter !== null && foreignBrandFilter.size === 0 ? (
            <>
              <p className="text-sm">No brands selected.</p>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs mt-1 text-primary hover:text-primary"
                onClick={() => setForeignBrandFilter(null)}
                data-testid="button-show-all-foreign-brands"
              >
                Show all brands
              </Button>
            </>
          ) : foreignDateFilter !== "all" ? (
            <>
              <p className="text-sm">
                No templates added in the {DATE_FILTER_OPTIONS.find((o) => o.id === foreignDateFilter)?.label.toLowerCase()}.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs mt-1 text-primary hover:text-primary"
                onClick={() => setForeignDateFilter("all")}
                data-testid="button-clear-foreign-date-filter"
              >
                Clear date filter
              </Button>
            </>
          ) : (
            <p className="text-sm">The selected brands have no static ad templates of their own.</p>
          )}
        </div>
      ) : (
        <div className="space-y-7">
          {foreignBrandGroups.map(({ brand: b, units }) => {
            const expanded = expandedForeignBrands.has(b.id);
            const hasMore = units.length > BUCKET_COLLAPSED_COUNT;
            const visibleUnits = expanded ? units : units.slice(0, BUCKET_COLLAPSED_COUNT);
            const hiddenCount = units.length - BUCKET_COLLAPSED_COUNT;
            return (
              <div key={b.id} data-testid={`section-foreign-brand-${b.id}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 bg-sky-500/15 text-sky-600 dark:text-sky-400">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold tracking-tight" data-testid={`text-foreign-brand-label-${b.id}`}>
                          {b.name}
                        </h3>
                        <Badge
                          variant="secondary"
                          className="h-5 px-1.5 text-[10px] font-bold tabular-nums"
                          data-testid={`badge-foreign-brand-count-${b.id}`}
                        >
                          {units.length}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:block h-px flex-1 ml-3 opacity-30 bg-sky-500" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {visibleUnits.map((u) => (
                    <TemplateCard
                      key={u.id}
                      unit={u}
                      checked={selectedTemplateIds.has(u.id)}
                      disabled={formLocked}
                      ineligible={!hasUsableExampleImage(u)}
                      brandId=""
                      onToggle={() => toggleForeignTemplate(u)}
                    />
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-2.5 flex justify-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => toggleForeignBrandExpanded(b.id)}
                      data-testid={`button-foreign-brand-toggle-${b.id}`}
                    >
                      {expanded ? (
                        <>
                          View Less
                          <ChevronUp className="h-3.5 w-3.5" />
                        </>
                      ) : (
                        <>
                          View More (+{hiddenCount})
                          <ChevronDown className="h-3.5 w-3.5" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="text-ads-express-title">
            <Zap className="h-6 w-6 text-primary" />
            Ads Express
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Pick a persona, product, and angle. Check the templates you want. We render them.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="ads-express-header-actions">
            <AdsExpressStyleRulesDialog brandId={brandId} brand={brand} />
        {isAdmin && (
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={() => setCategoriesManagerOpen(true)}
            data-testid="button-manage-categories"
          >
            <Folder className="h-4 w-4" />
            Manage Categories
          </Button>
        )}
        </div>
      </div>

      {isLoadingAny ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : emptyResource ? (
        <EmptyResource brandId={brandId!} resource={emptyResource} />
      ) : (
        <>
          {/* Settings bar — logo, promoted item type, and aspect ratios */}
          <Card className="p-3 sm:px-4 sm:py-3 border-border/60" data-testid="bar-ads-express-settings">
            <div className="flex items-center gap-x-4 gap-y-3 flex-wrap">
              <label
                className="flex items-center gap-2 cursor-pointer select-none"
                data-testid="toggle-include-logo-wrap"
              >
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium">Include my logo</span>
                <Switch
                  checked={includeLogo}
                  onCheckedChange={setIncludeLogo}
                  disabled={formLocked}
                  data-testid="switch-include-logo"
                />
              </label>

              <div className="flex items-center gap-2 sm:ml-auto">
                <Crop className="h-4 w-4 text-muted-foreground" />
                <span
                  className="text-xs font-medium"
                  title="Pick one or more. Each ad renders once per selected ratio. Reframe individual tiles afterwards."
                >
                  Aspect ratios
                </span>
                <div
                  role="group"
                  aria-label="Aspect ratios"
                  className="inline-flex items-center rounded-lg border border-border bg-background p-1 shadow-sm"
                >
                  {AD_ASPECT_RATIO_OPTIONS.map((opt) => {
                    const ratio = opt.value;
                    const isActive = aspectRatios.includes(ratio);
                    return (
                      <button
                        key={ratio}
                        type="button"
                        role="checkbox"
                        aria-checked={isActive}
                        onClick={() =>
                          setAspectRatios((prev) => {
                            // Toggle, but never allow an empty selection.
                            const next = prev.includes(ratio)
                              ? prev.filter((r) => r !== ratio)
                              : [...prev, ratio];
                            return next.length > 0 ? next : prev;
                          })
                        }
                        disabled={formLocked}
                        className={[
                          "px-3 h-8 rounded-md text-xs font-semibold transition-colors min-w-[52px]",
                          isActive
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted",
                          formLocked ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
                        ].join(" ")}
                        data-testid={`button-aspect-${ratio.replace(":", "x")}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </Card>

          <Card className="relative overflow-hidden p-6 space-y-5 border-border/60 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_-12px_rgba(0,0,0,0.6)] bg-gradient-to-b from-card to-muted/30 dark:from-card dark:to-background/40">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/[0.06] blur-3xl"
            />
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
                  <Wand2 className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    All three are required before you can generate ads
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {filterByAngles && <AdsExpressItemFilterHelp key={promotedItemType} isOfferMode={isOfferMode} />}
              <div
                className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm"
                data-testid="toggle-promoted-item"
              >
                <Button
                  type="button"
                  size="sm"
                  variant={isOfferMode ? "ghost" : "default"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => {
                    if (formLocked) return;
                    if (promotedItemType !== "product") releaseHistoricalAttachment();
                    setPromotedItemType("product");
                    setAngleId("");
                  }}
                  disabled={formLocked}
                  data-testid="toggle-promoted-product"
                >
                  <Package className="h-3 w-3" /> Product
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={isOfferMode ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => {
                    if (formLocked) return;
                    if (promotedItemType !== "offer") releaseHistoricalAttachment();
                    setPromotedItemType("offer");
                    setAngleId("");
                  }}
                  disabled={formLocked}
                  data-testid="toggle-promoted-offer"
                >
                  <Sparkles className="h-3 w-3" /> Offer
                </Button>
              </div>
              </div>
            </div>
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
              <PickerSelect
                icon={<Users className="h-4 w-4" />}
                label="Persona"
                placeholder="Choose persona"
                value={personaId}
                onChange={(next) => {
                  if (next !== personaId) releaseHistoricalAttachment();
                  setPersonaId(next);
                  setSelectedPersonaCharacterId("");
                  if (next !== personaId) setAngleId("");
                }}
                options={personas.map((p) => ({
                  value: p.id,
                  label: p.name,
                  imageUrl: (p as { imageUrl?: string | null }).imageUrl ?? null,
                }))}
                testId="select-persona"
                searchable
                disabled={formLocked}
              />
              <div className="relative min-w-0">
              {isOfferMode ? (
                <PickerSelect
                  icon={<Sparkles className="h-4 w-4" />}
                  label="Offer"
                  placeholder={offers.length === 0 ? "No offers yet — add one in Offers" : promotedItemFilterEmpty ? promotedItemEmptyLabel : "Choose offer"}
                  value={offerId}
                  selectedDisplay={offerId ? {
                    value: offerId, label: offers.find((o) => o.id === offerId)?.name ?? `Previous offer (${offerId})`,
                    description: offers.find((o) => o.id === offerId)?.shortDescription ?? null,
                  } : undefined}
                  onChange={(next) => {
                    if (next !== offerId) releaseHistoricalAttachment();
                    setOfferId(next);
                    if (next !== offerId) setAngleId("");
                  }}
                  options={eligibleOffers.map((o) => ({
                    value: o.id,
                    label: o.name,
                    description: (o as any).shortDescription || (o as any).summary || null,
                  }))}
                  testId="select-offer"
                  searchable
                  disabled={formLocked || offers.length === 0}
                />
              ) : (
                <PickerSelect
                  icon={<Package className="h-4 w-4" />}
                  label="Product"
                  placeholder={promotedItemFilterEmpty ? promotedItemEmptyLabel : "Choose product"}
                  value={productId}
                  selectedDisplay={productId ? {
                    value: productId, label: products.find((p) => p.id === productId)?.name ?? `Previous product (${productId})`,
                  } : undefined}
                  onChange={(next) => {
                    if (next !== productId) releaseHistoricalAttachment();
                    setProductId(next);
                    if (next !== productId) setAngleId("");
                  }}
                  options={[...eligibleProducts]
                    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                    .map((p) => {
                      const media = p.mediaJson;
                      const first = Array.isArray(media)
                        ? media.find((entry): entry is string => typeof entry === "string" && entry.trim().length > 0) ?? null
                        : null;
                      return { value: p.id, label: p.name, imageUrl: first };
                    })}
                  testId="select-product"
                  searchable
                  disabled={formLocked}
                />
              )}
              {promotedItemFilterEmpty && (
                <div className="mt-1.5 text-xs" data-testid="text-promoted-item-filter-empty">
                  <button type="button" className="text-primary hover:underline focus-visible:underline py-1"
                    disabled={formLocked} data-testid="button-switch-to-custom-angle"
                    onClick={() => setAngleMode("custom")}>
                    Use a custom concept angle instead
                  </button>
                </div>
              )}
              </div>
              {liveGen?.generation?.personaCharacterSelectorEnabled === true && (
                <div className="md:col-span-2">
                  <Badge
                    variant={liveGen.generation.selectedPersonaCharacterNameSnapshot ? "secondary" : "outline"}
                    className="w-fit text-[10px]"
                    data-testid={
                      liveGen.generation.selectedPersonaCharacterNameSnapshot
                        ? "badge-resumed-run-character"
                        : "badge-resumed-run-character-unavailable"
                    }
                  >
                    {liveGen.generation.selectedPersonaCharacterNameSnapshot
                      ? `Run character: ${liveGen.generation.selectedPersonaCharacterNameSnapshot}`
                      : "Character unavailable"}
                  </Badge>
                </div>
              )}
              {characterPickerAvailable && personaId && (
                <div
                  className="space-y-2.5 rounded-xl border border-border/60 bg-muted/[0.18] p-3 md:col-span-2"
                  data-testid="card-ads-express-character-selector"
                >
                  <div>
                    <p className="text-sm font-semibold">Persona character</p>
                    <p className="text-xs text-muted-foreground">
                      {loadingPersonaCharacters
                        ? "Loading ready characters…"
                        : readyPersonaCharacters.length === 0
                          ? `No ready characters are available for ${persona?.name ?? "this persona"}.`
                          : "Choose the ready character to use for this run."}
                    </p>
                  </div>
                  {loadingPersonaCharacters && (
                    <div
                      className="flex items-center gap-2 text-xs text-muted-foreground"
                      data-testid="text-ads-express-character-loading"
                    >
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading characters…
                    </div>
                  )}
                  {!loadingPersonaCharacters && selectedPersonaCharacter && (
                    <div
                      className="grid grid-cols-[56px_minmax(0,1fr)] items-center gap-2.5 rounded-lg border border-primary/30 bg-background p-2"
                      data-testid="selected-ads-express-character-preview"
                    >
                      <img
                        src={selectedPersonaCharacter.avatarUrl!}
                        alt={`Selected character: ${selectedPersonaCharacter.name}`}
                        className="aspect-square w-full rounded-md object-cover"
                        data-testid="image-selected-ads-express-character"
                      />
                      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <p className="min-w-0 truncate text-sm font-semibold">
                          {selectedPersonaCharacter.name}
                        </p>
                        <Badge variant="secondary" className="h-5 w-fit gap-1 px-1.5 text-[10px]">
                          Selected <Check className="h-3 w-3" />
                        </Badge>
                        <Button asChild type="button" variant="ghost" size="sm" className="h-6 w-fit px-1 text-xs underline-offset-4 hover:underline">
                          <Link
                            href={`/brands/${brandId}/personas/${personaId}?characterId=${encodeURIComponent(selectedPersonaCharacter.id)}&from=ads-express`}
                            data-testid="button-manage-selected-ads-express-character"
                          >
                            Manage in Personas
                          </Link>
                        </Button>
                      </div>
                    </div>
                  )}
                  {!loadingPersonaCharacters && readyPersonaCharacters.length > 0 && (
                    <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10">
                      {readyPersonaCharacters.map((character) => {
                        const selected = character.id === selectedPersonaCharacterId;
                        return (
                          <button
                            key={character.id}
                            type="button"
                            disabled={formLocked}
                            onClick={() => setSelectedPersonaCharacterId(character.id)}
                            className={`relative min-w-0 overflow-hidden rounded-lg border text-left transition-all disabled:opacity-60 ${
                              selected
                                ? "border-primary ring-2 ring-primary/40"
                                : "border-border/50 bg-background hover:border-primary/40"
                            }`}
                            data-testid={`ads-express-character-card-${character.id}`}
                          >
                            <img
                              src={character.avatarUrl!}
                              alt={character.name}
                              className="aspect-square w-full object-cover"
                              loading="lazy"
                            />
                            <p className="truncate bg-background px-1.5 py-1 text-[10px] font-semibold leading-tight" title={character.name}>
                              {character.name}
                            </p>
                            {selected && (
                              <span className="absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                <Check className="h-2.5 w-2.5" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {!loadingPersonaCharacters && unavailablePersonaCharacterCount > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="text-unavailable-ads-express-characters">
                      {unavailablePersonaCharacterCount} {unavailablePersonaCharacterCount === 1 ? "character is" : "characters are"} unavailable until a ready headshot is generated.
                    </p>
                  )}
                  {!loadingPersonaCharacters && readyPersonaCharacters.length === 0 && (
                    <Button asChild type="button" variant="outline" size="sm">
                      <Link
                        href={`/brands/${brandId}/personas/${personaId}?from=ads-express`}
                        data-testid="button-manage-empty-ads-express-characters"
                      >
                        Manage characters in Personas
                      </Link>
                    </Button>
                  )}
                </div>
              )}
              {/* Task #744 — variant picker as its own full-width row, under
                  persona/product and above the angle selector. With no variants
                  it renders null so the layout stays identical to before. */}
              {!isOfferMode && product && (
                <VariantPicker
                  product={product}
                  value={selectedVariantName}
                  onChange={setSelectedVariantName}
                  testIdPrefix="ads-express-variant"
                  className="md:col-span-2"
                />
              )}
              {/* Task #1413 — KC-style inline product reference image picker
                  (beta-gated). Fully separate from the upload-references
                  Advanced section: click order picks which of the product's
                  own library photos anchor the run (#1 = main shot). Hidden
                  with ≤1 image (nothing to choose — the default photo is
                  used either way, KC parity). */}
              {productRefLibraryEnabled &&
                !!(isOfferMode ? offer : product) &&
                productLibraryImages.length > 1 && (
                <div
                  className="md:col-span-2 rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2.5"
                  data-testid="panel-product-library"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-xs font-semibold">
                      {isOfferMode ? "Offer reference images" : "Product reference images"}
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        click in order — #1 is the main shot
                      </span>
                    </p>
                    <p className="text-[11px] text-muted-foreground tabular-nums" data-testid="text-library-count">
                      {selectedLibraryImages.length}/{productLibraryImages.length} selected
                    </p>
                  </div>
                  {(() => {
                    const renderTile = (url: string, tileIdx: number) => {
                      const order = selectedLibraryImages.indexOf(url);
                      const checked = order >= 0;
                      const isMain = order === 0;
                      return (
                        <button
                          key={url}
                          type="button"
                          disabled={formLocked}
                          onClick={() => {
                            setSelectedLibraryImages((prev) => {
                              const idx = prev.indexOf(url);
                              if (idx >= 0) return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
                              return [...prev, url];
                            });
                          }}
                          className={`group relative h-20 w-20 rounded-lg border overflow-hidden bg-background transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed ${
                            checked
                              ? isMain
                                ? "ring-2 ring-primary border-primary shadow-md shadow-primary/20 scale-[1.02]"
                                : "ring-2 ring-primary/60 border-primary/60 shadow-sm"
                              : "opacity-70 hover:opacity-100 hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5"
                          }`}
                          aria-pressed={checked}
                          data-testid={`button-library-image-${tileIdx}`}
                          title={
                            checked
                              ? isMain
                                ? "Main product shot (#1) — click to remove"
                                : `Reference #${order + 1} — click to remove`
                              : "Add as product reference (click order matters)"
                          }
                        >
                          <img src={thumbUrl(url, 160)!} onError={thumbFallback(url)} loading="lazy" alt="Product library" className="h-full w-full object-cover" />
                          {checked && (
                            <div
                              className={`absolute top-1 right-1 rounded-full h-5 min-w-5 px-1 flex items-center justify-center shadow text-[11px] font-bold tabular-nums ${
                                isMain
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-background text-foreground border border-primary/60"
                              }`}
                            >
                              {isMain ? "#1" : order + 1}
                            </div>
                          )}
                          {isMain && (
                            <div className="absolute bottom-0 inset-x-0 bg-primary/90 text-primary-foreground text-[10px] font-semibold text-center py-0.5">
                              main
                            </div>
                          )}
                        </button>
                      );
                    };
                    const { adgen, media } = productLibraryGroups;
                    return (
                      <div className="space-y-2.5">
                        {adgen.length > 0 && (
                          <div className="space-y-1.5">
                            <p
                              className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                              data-testid="text-library-section-adgen"
                            >
                              Ad gen images
                            </p>
                            <div className="flex flex-wrap gap-2.5">
                              {adgen.map((url, i) => renderTile(url, i))}
                            </div>
                          </div>
                        )}
                        {media.length > 0 && (
                          <div className="space-y-1.5">
                            <p
                              className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                              data-testid="text-library-section-media"
                            >
                              Product photos
                            </p>
                            <div className="flex flex-wrap gap-2.5">
                              {media.map((url, i) => renderTile(url, adgen.length + i))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {selectedLibraryImages.length === 0 && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="text-library-empty">
                      No images selected — the {isOfferMode ? "offer" : "product"}&apos;s default photo will be used.
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <div className="inline-flex items-center rounded-lg border border-border/60 bg-muted/40 p-0.5">
                  <button
                    type="button"
                    onClick={() => setAngleMode("saved")}
                    disabled={formLocked}
                    className={[
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                      angleMode === "saved"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    ].join(" ")}
                    data-testid="button-angle-mode-saved"
                  >
                    <BookOpen className="h-3.5 w-3.5" /> Saved angle
                  </button>
                  <button
                    type="button"
                    onClick={() => setAngleMode("custom")}
                    disabled={formLocked}
                    className={[
                      "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors",
                      angleMode === "custom"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    ].join(" ")}
                    data-testid="button-angle-mode-custom"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Custom concept angle
                  </button>
                </div>

                {angleMode === "saved" ? (
                  <div className={narrativeFunnelStageEnabled ? "flex flex-col sm:flex-row gap-3" : ""}>
                    {narrativeFunnelStageEnabled && (
                      <div className="w-full sm:w-56 shrink-0">
                        <PickerSelect
                          icon={<Sliders className="h-4 w-4" />}
                          label="Funnel Stage"
                          placeholder="All stages"
                          value={funnelStage}
                          onChange={(value) => changeFunnelStage(value as SavedAngleStage)}
                          onClear={funnelStage !== "all" ? () => changeFunnelStage("all") : undefined}
                          options={SAVED_ANGLE_STAGE_OPTIONS}
                          testId="select-funnel-stage"
                          optional
                          disabled={formLocked}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <PickerSelect
                        icon={<Sparkles className="h-4 w-4" />}
                        label="Narrative Angle"
                        placeholder={
                          !personaId || !promotedItemId
                            ? `Pick persona & ${isOfferMode ? "offer" : "product"} first`
                            : savedAngleChoices.length === 0
                              ? "No saved angles for this pair"
                              : "Choose narrative angle"
                        }
                        value={angleId}
                        selectedDisplay={getHistoricalAdsExpressAngleDisplay({
                          preservingHistoricalAttachment, angleId, angles,
                          angleNameSnapshot: liveGen?.generation?.id === activeGenerationId
                            ? liveGen.generation.angleNameSnapshot : null,
                        })}
                        onChange={setAngleId}
                        groups={angleGroups}
                        testId="select-angle"
                        searchable
                        wrapDescription
                        disabled={formLocked || !personaId || !promotedItemId || savedAngleChoices.length === 0}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="ads-express-concept" className="text-xs font-medium">
                      Custom concept angle
                    </Label>
                    <p className="text-[11px] text-muted-foreground mb-1.5">
                      Describe your narrative concept. On Generate, we'll transform it into a saved narrative angle for this persona &amp; {isOfferMode ? "offer" : "product"}.
                    </p>
                    <Textarea
                      id="ads-express-concept"
                      placeholder={conceptPlaceholder}
                      value={concept}
                      onChange={(e) => setConcept(e.target.value)}
                      rows={4}
                      className="resize-none text-sm"
                      disabled={formLocked}
                      data-testid="input-concept"
                    />
                  </div>
                )}
              </div>
              {/* Task #744 — bottom-align the sale picker so it keeps its natural
                  height and matches the narrative-angle picker, instead of
                  stretching to fill the taller (toggle + angle) left column. */}
              <div className="flex flex-col justify-end">
                <PickerSelect
                  icon={<Percent className="h-4 w-4" />}
                  label="Sale / Discount (optional)"
                  placeholder={
                    salesDiscounts.length === 0
                      ? "No sales yet — add one in Sales & Discounts"
                      : "Choose a sale to weave into the ad"
                  }
                  value={salesDiscountId === "" ? "__none__" : salesDiscountId}
                  onChange={(v) => setSalesDiscountId(v === "__none__" ? "" : v)}
                  options={[
                    { value: "__none__", label: "No sale — render at full price", description: null },
                    ...salesDiscounts.map((s) => ({
                      value: s.id,
                      label: s.discountValue ? `${s.name} · ${s.discountValue}` : s.name,
                      description: s.customerFacingCopy || s.promotionSummary || (s.saleType ? s.saleType.replace(/_/g, " ") : null),
                    })),
                  ]}
                  testId="select-sales-discount"
                  disabled={formLocked || salesDiscounts.length === 0}
                />
              </div>
            </div>

            <div className="relative flex items-center gap-2 pt-4 border-t border-dashed border-border/50 flex-wrap">
              <Folder className="h-3 w-3 text-muted-foreground/70" />
              <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">
                Project
              </span>
              <Select
                value={projectId === "" ? "none" : projectId}
                onValueChange={(v) => {
                  if (v === "__new") { setNewProjectOpen(true); return; }
                  setProjectId(v === "none" ? "" : v);
                }}
                disabled={formLocked}
              >
                <SelectTrigger className="h-7 text-xs w-52 border-transparent bg-transparent hover:bg-muted/50 focus:bg-background shadow-none px-2" data-testid="select-project">
                  <SelectValue placeholder="(no project)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(no project)</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                  <SelectItem value="__new" data-testid="select-project-new">
                    + New project…
                  </SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[11px] text-muted-foreground/60 ml-auto">
                Saved ads from this run will be filed here.
              </span>
            </div>

            {/* Advanced settings — optional visual reference images + extra
                context. Collapsed by default; when no references are attached
                the run is byte-for-byte identical to a normal run. */}
            <div className="relative pt-4 border-t border-dashed border-border/50">
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                aria-expanded={advancedOpen}
                data-testid="button-toggle-advanced-settings"
              >
                {advancedOpen ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <Sliders className="h-3.5 w-3.5" />
                Advanced settings
                {(references.length > 0 || additionalContext.trim()) && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                    {references.length > 0
                      ? `${references.length} reference${references.length === 1 ? "" : "s"}`
                      : "context"}
                  </Badge>
                )}
              </button>

              {advancedOpen && (
                <div className="mt-4 space-y-4" data-testid="panel-advanced-settings">
                  {adsExpressImageEngineChoiceEnabled && (
                    <AdsExpressImageEngineSelector
                      brandId={brandId ?? ""}
                      choice={imageEngineChoice}
                      has916={aspectRatios.includes("9:16")}
                      disabled={formLocked}
                      onChoiceChange={setImageEngineChoice}
                    />
                  )}
                  {/* Task #1331 — reference images are a beta feature gated per
                      user per brand; hidden entirely when the flag is OFF.
                      Additional context below stays available to everyone. */}
                  {referenceImagesEnabled && (
                  <div>
                    <Label className="text-xs font-medium">Visual reference images</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {templatePhotoFeatures.enabled ? (
                        <>
                          Attach up to {MAX_AD_REFERENCES} ordinary references and {MAX_TEMPLATE_PHOTO_REFERENCES} template photos.
                          Ordinary references steer the look and feel or add a product to feature. Template photos replace the template&apos;s photo content.
                        </>
                      ) : (
                        <>
                          Attach up to {MAX_AD_REFERENCES} images. Each can steer the look &amp;
                          feel (background, lighting, color, mood) or add a product that should
                          appear in the ad {"\u2014"} pick a purpose per image, or leave it on Auto.
                        </>
                      )}
                    </p>
                    {templatePhotoFeatures.enabled && (
                      <div className="mt-3 rounded-md border border-border/60 bg-muted/20 p-3 space-y-3" data-testid="panel-template-photo-sources">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Photo source">
                            {([
                              ["upload", "Upload"],
                              ["product", "Product photos"],
                              ["adgen", "Ad Gen images"],
                              ...(isOfferMode ? [["offer", "Offer photos"]] : []),
                            ] as Array<[typeof templatePhotoSource, string]>)
                              .filter(([source]) => source === "upload" || templatePhotoFeatures.libraryEnabled)
                              .map(([source, label]) => (
                                <Button
                                  key={source}
                                  type="button"
                                  size="sm"
                                  variant={templatePhotoSource === source ? "default" : "outline"}
                                  className="h-7 px-2.5 text-xs"
                                  disabled={formLocked || uploadingRef}
                                  onClick={() => {
                                    setTemplatePhotoSource(source);
                                    setPendingLibraryPhotos([]);
                                  }}
                                  data-testid={`button-template-photo-source-${source}`}
                                >
                                  {label}
                                </Button>
                              ))}
                          </div>
                          <span className="text-[11px] tabular-nums text-muted-foreground" data-testid="text-template-photo-count">
                            {templatePhotoCount} of {MAX_TEMPLATE_PHOTO_REFERENCES} template photos
                            <span className="mx-1.5">·</span>
                            {ordinaryReferenceCount} of {MAX_AD_REFERENCES} ordinary references
                          </span>
                        </div>
                        {templatePhotoFeatures.libraryEnabled && templatePhotoSource !== "upload" && (
                          <div className="space-y-2">
                            {templatePhotoLibraryItems.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {templatePhotoLibraryItems.map((url, index) => {
                                  const order = pendingLibraryPhotos.indexOf(url);
                                  return (
                                    <button
                                      key={`${url}-${index}`}
                                      type="button"
                                      disabled={formLocked}
                                      onClick={() => setPendingLibraryPhotos((previous) =>
                                        order >= 0
                                          ? previous.filter((item) => item !== url)
                                          : [...previous, url],
                                      )}
                                      className={`relative h-16 w-16 overflow-hidden rounded-md border transition-colors ${
                                        order >= 0 ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/50"
                                      }`}
                                      aria-label={`${order >= 0 ? "Remove" : "Select"} photo ${index + 1}`}
                                      data-testid={`button-select-template-photo-${index}`}
                                    >
                                      <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                      {order >= 0 && (
                                        <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                                          {order + 1}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-[11px] text-muted-foreground">
                                No photos are available for this source.
                              </p>
                            )}
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] text-muted-foreground">
                                {pendingLibraryPhotos.length} selected. Add selected photos in click order.
                              </p>
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="h-7 text-xs"
                                disabled={formLocked || pendingLibraryPhotos.length === 0}
                                onClick={addTemplatePhotoLibrarySelection}
                                data-testid="button-add-selected-template-photos"
                              >
                                Add selected
                              </Button>
                            </div>
                          </div>
                        )}
                        {templatePhotoSource === "upload" && (
                          <p className="text-[11px] text-muted-foreground">
                            Upload several photos together to add them in order. {ADS_EXPRESS_REFERENCE_UPLOAD_GUIDANCE}
                          </p>
                        )}
                      </div>
                    )}
                    {templatePhotoSlotPlans.length > 0 && (
                      <div className="mt-2 space-y-1 rounded-md bg-muted/30 px-2.5 py-2" data-testid="panel-template-photo-slot-plan">
                        {templatePhotoSlotPlans.map((plan) => (
                          <div key={plan.id} className="text-[11px] text-muted-foreground">
                            <span className="font-medium text-foreground">{plan.name}:</span>{" "}
                            {plan.imageSlotsUsed} of {plan.limit} image slots used ({plan.engine})
                            {` · ${plan.remaining} additional ordinary references available`}
                            {plan.dropped > 0 && ` · the last ${plan.dropped} template photos will not be attached to this template`}
                            {plan.reservePack && " · includes 3 identity-pack reservations"}
                            <p className="text-[10px]">
                              Template {plan.breakdown.template} · logo {plan.breakdown.logo} · product photos {plan.breakdown.product}
                              {" "}· font {plan.breakdown.font} · persona {plan.breakdown.persona} · ordinary reference slots {plan.breakdown.ordinary}
                              {" "}· template photos {plan.breakdown.templatePhotos}
                            </p>
                          </div>
                        ))}
                        <p className="text-[10px] text-muted-foreground">
                          Includes the template, model-sent logo, product photos, font specimen, active persona identity pack and eligible template photos.
                          {" "}Person / identity uses 2 slots; a product reference replaces the primary product photo and its extras.
                          {" "}Auto reserves one slot until its purpose is resolved. Exact-overlay logos use no model slot.
                          {" "}The smallest capacity across selected templates and ratios applies.
                        </p>
                        <p className="text-[11px]" data-testid="ordinary-reference-counter">
                          {ordinaryReferenceCount} of {MAX_AD_REFERENCES} ordinary references · {Math.max(0, remainingReferenceCapacity)} remaining
                        </p>
                      </div>
                    )}
                    {imageBudgetError && (
                      <p className="mt-2 text-xs text-destructive" role="alert" data-testid="reference-budget-error">{imageBudgetError}</p>
                    )}

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {references.map((ref, idx) => (
                        <div
                          key={`${ref.url}-${idx}`}
                          className="rounded-lg border border-border/70 bg-background/60 p-2 space-y-2"
                          data-testid={`card-reference-${idx}`}
                        >
                          <div className="relative">
                            <img
                              src={ref.url}
                              alt={`Reference ${idx + 1}`}
                              className="w-full h-24 object-cover rounded-md"
                              data-testid={`img-reference-${idx}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeReference(idx)}
                              disabled={formLocked}
                              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 disabled:opacity-50"
                              aria-label={`Remove reference ${idx + 1}`}
                              data-testid={`button-remove-reference-${idx}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <Select
                            value={ref.purpose}
                            onValueChange={(v) =>
                              updateReferencePurpose(
                                idx,
                                v as "auto" | "environment" | "product" | "product_environment" | "person_identity" | "template_photo",
                              )
                            }
                            disabled={formLocked}
                          >
                            <SelectTrigger
                              className="h-7 text-xs"
                              data-testid={`select-reference-purpose-${idx}`}
                            >
                              <SelectValue placeholder="Use as…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto" data-testid={`option-reference-purpose-auto-${idx}`}>
                                Auto — let us decide
                              </SelectItem>
                              <SelectItem value="environment" data-testid={`option-reference-purpose-environment-${idx}`}>
                                Environment / setting
                              </SelectItem>
                              <SelectItem value="product" data-testid={`option-reference-purpose-product-${idx}`}>
                                Product to feature
                              </SelectItem>
                              <SelectItem value="product_environment" data-testid={`option-reference-purpose-product-environment-${idx}`}>
                                Product + environment
                              </SelectItem>
                              {personIdentityReferencesEnabled && (
                                <SelectItem value="person_identity" data-testid={`option-reference-purpose-person-identity-${idx}`}>
                                  Person / identity
                                </SelectItem>
                              )}
                              {templatePhotoFeatures.enabled && (
                                <SelectItem value="template_photo" data-testid={`option-reference-purpose-template-photo-${idx}`}>
                                  Photo for template (match closely)
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground/80 leading-snug">
                            {ref.purpose === "product"
                              ? "This product will appear in the ad."
                              : ref.purpose === "environment"
                                ? "Steers background, lighting & mood only."
                                : ref.purpose === "product_environment"
                                  ? "This product appears in the ad AND its setting becomes the scene."
                                  : ref.purpose === "person_identity"
                                    ? "Preserves this exact identifiable person — not just appearance, vibe, or setting. If the photo also shows a product, add it separately as Product; this purpose carries only the person's identity."
                                    : ref.purpose === "template_photo"
                                      ? "Replaces photo content in the selected template. Multiple photos are applied in order."
                                  : "We'll detect: product shot → featured product; scene → environment."}
                          </p>
                          {ref.purpose === "person_identity" && (
                            <label
                              className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50/60 p-2 text-[10px] leading-snug text-amber-950 dark:bg-amber-950/20 dark:text-amber-100"
                              data-testid={`identity-consent-${idx}`}
                            >
                              <Switch
                                checked={ref.identityConsent === true}
                                onCheckedChange={(checked) =>
                                  setReferences((prev) =>
                                    prev.map((r, i) =>
                                      i === idx ? { ...r, identityConsent: checked } : r,
                                    ),
                                  )
                                }
                                disabled={formLocked}
                                data-testid={`switch-reference-identity-consent-${idx}`}
                              />
                              <span>
                                I confirm I have permission to use this person&apos;s likeness. The image
                                will be used to preserve identity, not to infer sensitive traits.
                              </span>
                            </label>
                          )}
                          <Input
                            value={ref.note}
                            onChange={(e) => updateReferenceNote(idx, e.target.value)}
                            placeholder="Optional note (e.g. warm sunset tones)"
                            disabled={formLocked}
                            className="h-7 text-xs"
                            data-testid={`input-reference-note-${idx}`}
                          />
                        </div>
                      ))}

                      {(!templatePhotoFeatures.enabled || templatePhotoSource === "upload") &&
                        (templatePhotoFeatures.enabled
                          ? references.length < MAX_AD_REFERENCES + MAX_TEMPLATE_PHOTO_REFERENCES
                          : ordinaryReferenceCount < MAX_AD_REFERENCES) && (
                        <button
                          type="button"
                          onClick={() => refFileInputRef.current?.click()}
                          disabled={formLocked || uploadingRef}
                          className="flex flex-col items-center justify-center gap-1.5 h-[136px] rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-muted/40 text-muted-foreground transition-colors disabled:opacity-50"
                          data-testid="button-add-reference"
                        >
                          {uploadingRef ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <ImagePlus className="h-5 w-5" />
                          )}
                          <span className="text-xs font-medium">
                            {uploadingRef ? "Uploading…" : "Add reference"}
                          </span>
                          <span className="text-[10px] text-muted-foreground/70">
                            {ADS_EXPRESS_REFERENCE_UPLOAD_GUIDANCE}
                          </span>
                        </button>
                      )}

                    </div>

                    <input
                      ref={refFileInputRef}
                      type="file"
                      accept={ADS_EXPRESS_REFERENCE_ACCEPT}
                      multiple
                      className="hidden"
                      onChange={handleReferenceInputChange}
                      data-testid="input-reference-file"
                    />
                  </div>
                  )}

                  {/* Task #1536 — beta Google Font picker (user-grant flag;
                      hidden entirely when OFF). "" = no font → the run is
                      byte-for-byte identical to a normal run. */}
                  {googleFontAdsEnabled && (
                    <div>
                      <Label className="text-xs font-medium flex items-center gap-1.5">
                        <Type className="h-3.5 w-3.5 text-muted-foreground" />
                        Ad font
                      </Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Optional font the ad&apos;s text should be rendered in.
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <Select
                          value={adFontFamily || "none"}
                          onValueChange={(v) => {
                            // Brand-group items carry a "brand::" prefix so
                            // Radix item values stay unique when the same
                            // family also appears in a catalog group below.
                            // Stripping it here means picking a brand font is
                            // indistinguishable from picking the catalog copy
                            // (same state, same submit payload).
                            const raw = v.startsWith("brand::") ? v.slice("brand::".length) : v;
                            const fam = raw === "none" ? "" : raw;
                            setAdFontFamily(fam);
                            if (fam) ensureGoogleFontsLoaded([fam]);
                          }}
                          onOpenChange={(open) => {
                            // Load every catalog family the first time the
                            // dropdown opens so each option renders in its own
                            // typeface (one batched stylesheet; the loader
                            // dedupes, and browsers only download the font
                            // files for glyphs actually shown).
                            if (open) ensureGoogleFontsLoaded(GOOGLE_FONT_CATALOG.map((f) => f.family));
                          }}
                          disabled={formLocked}
                        >
                          <SelectTrigger className="h-8 text-xs w-64" data-testid="select-ad-font">
                            <SelectValue placeholder="No specific font" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="none" data-testid="option-ad-font-none">
                              No specific font
                            </SelectItem>
                            <SelectSeparator />
                            {/* Task #1609 — brand Design System fonts (Google-
                                source only) surfaced at the top. Hidden when
                                the brand has none saved so the picker stays
                                exactly as before. */}
                            {brandAdFonts.length > 0 && (
                              <SelectGroup>
                                <SelectLabel className="-mx-1 mb-1 px-3 py-1.5 bg-primary/10 text-[10px] font-semibold uppercase tracking-wider text-primary">
                                  Brand fonts
                                </SelectLabel>
                                {brandAdFonts.map(({ family, roleLabel }) => (
                                  <SelectItem
                                    key={`brand::${family}`}
                                    value={`brand::${family}`}
                                    data-testid={`option-ad-font-brand-${family.replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    <span style={{ fontFamily: `"${family}", sans-serif` }}>
                                      {family}
                                    </span>
                                    {roleLabel && (
                                      <span className="ml-1.5 text-[10px] text-muted-foreground">
                                        {roleLabel}
                                      </span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            {(["sans-serif", "serif", "display", "handwriting", "monospace"] as const).map((cat) => (
                              <SelectGroup key={cat}>
                                <SelectLabel className="-mx-1 mb-1 mt-1 px-3 py-1.5 bg-muted/80 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                  {cat === "sans-serif" ? "Sans serif" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                                </SelectLabel>
                                {GOOGLE_FONT_CATALOG.filter((f) => f.category === cat).map((f) => (
                                  <SelectItem
                                    key={f.family}
                                    value={f.family}
                                    data-testid={`option-ad-font-${f.family.replace(/\s+/g, "-").toLowerCase()}`}
                                  >
                                    <span style={{ fontFamily: `"${f.family}", ${cat === "handwriting" ? "cursive" : cat === "display" ? "sans-serif" : cat}` }}>
                                      {f.family}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                          </SelectContent>
                        </Select>
                        {adFontFamily && (
                          <span
                            className="text-sm text-muted-foreground truncate"
                            style={{ fontFamily: `"${adFontFamily}", sans-serif` }}
                            data-testid="text-ad-font-preview"
                          >
                            The quick brown fox
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="additional-context" className="text-xs font-medium">
                      Additional context
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Optional freeform guidance for the visual environment.
                    </p>
                    <Textarea
                      id="additional-context"
                      value={additionalContext}
                      onChange={(e) => setAdditionalContext(e.target.value)}
                      placeholder="e.g. Shoot in a sunlit kitchen with natural wood textures and soft morning light."
                      disabled={formLocked}
                      rows={3}
                      className="mt-2 text-xs"
                      data-testid="input-additional-context"
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            {/* Task #1654 — master-only + flag-gated cross-brand browser:
                the current brand's section shows by default with an "Other
                brands" button inline in its toolbar (left of search);
                clicking it swaps in the cross-brand browser, whose toolbar
                carries "Back to this brand" in the same spot. Both views
                feed one shared selection. Gated off ⇒ the bare section
                renders exactly as before (the button is gated out). */}
            {crossBrandTabsEnabled && showOtherBrands
              ? allBrandsTemplatesSection
              : thisBrandTemplatesSection}
          </Card>

          {/* Task #2117 — run-queue panel (flag-gated). Lists Running +
              Queued runs with per-run View/Cancel. */}
          {runQueueEnabled && brandId && queuedRuns.length > 0 && (
            <AdsExpressRunQueuePanel
              brandId={brandId}
              variant="standard"
              runs={queuedRuns}
              activeGenerationId={activeGenerationId}
              onSelect={(id) => {
                setLocalOverrides({});
                setActiveGenerationId(id);
                hydratedFromUrlRef.current = true;
              }}
            />
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap sticky bottom-4 z-10">
            <Card className="px-4 py-2.5 flex items-center gap-3 shadow-md">
              {isRunning || jobs.length > 0 ? (
                <>
                  <Progress value={progressPct} className="w-40 h-1.5" />
                  <span className="text-xs text-muted-foreground tabular-nums" data-testid="text-progress-summary">
                    {counts.done} of {counts.total} done
                    {counts.failed > 0 ? `, ${counts.failed} failed` : ""}
                  </span>
                </>
              ) : selectedTemplateIds.size > 0 ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold text-foreground tabular-nums shrink-0">
                    {selectedTemplateIds.size} format{selectedTemplateIds.size === 1 ? "" : "s"} selected
                  </span>
                  <span className="text-xs text-muted-foreground truncate hidden md:inline" data-testid="text-selected-names">
                    · {templatesForDisplay
                      .filter((u) => selectedTemplateIds.has(u.id))
                      .slice(0, 4)
                      .map((u) => shortNameOf(u.slug, u.name))
                      .join(", ")}
                    {selectedTemplateIds.size > 4 ? `, +${selectedTemplateIds.size - 4} more` : ""}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Pick inputs above and choose templates to begin
                </span>
              )}
            </Card>
            <div className="flex items-center gap-2">
              {imageBudgetError && (
                <p className="max-w-md text-xs text-destructive" role="alert" data-testid="generation-budget-error">
                  {imageBudgetError}
                </p>
              )}
              {isRunning && (
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="rounded-full"
                  data-testid="button-cancel"
                >
                  <X className="h-4 w-4 mr-1.5" />
                  Cancel
                </Button>
              )}
              {jobs.filter((j) => j.status === "completed" && !!j.imageUrl && !!j.generatedAdId && !j.isSaved).length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleSaveAll}
                  disabled={savingAll}
                  className="rounded-full"
                  data-testid="button-save-all"
                >
                  {savingAll ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Bookmark className="h-4 w-4 mr-1.5" />
                  )}
                  Save all
                </Button>
              )}
              {jobs.filter((j) => j.status === "completed").length > 0 && !isRunning && (
                <Button
                  variant="outline"
                  onClick={downloadAll}
                  className="rounded-full"
                  data-testid="button-download-all"
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Download all
                </Button>
              )}
              <Button
                onClick={handleGenerate}
                disabled={!canGenerate || formLocked || submitting || sameAsLastRun}
                className="rounded-full px-5"
                data-testid="button-generate"
                title={
                  sameAsLastRun
                    ? "These ads were just generated. Change a setting above (persona, product, angle, ad units, aspect ratio, or project) to start a new run."
                    : undefined
                }
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    {angleMode === "custom" ? "Generating angle…" : "Starting…"}
                  </>
                ) : formLocked ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating...</>
                ) : sameAsLastRun ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-1.5" />
                    Generated
                  </>
                ) : selectedTemplateIds.size === 0 ? (
                  <>
                    <Zap className="h-4 w-4 mr-1.5" />
                    Generate ads
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-1.5" />
                    Generate {selectedTemplateIds.size} ad{selectedTemplateIds.size === 1 ? "" : "s"}
                  </>
                )}
              </Button>
            </div>
          </div>

          {jobs.some((j) => j.aspectRatio === "9:16") && (
            <div className="flex justify-end">
              <SafeZoneToggle active={showSafeZone} onToggle={() => setShowSafeZone((v) => !v)} />
            </div>
          )}

          {jobs.length > 0 && (
            <div ref={jobsGridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="grid-jobs">
              {jobs.flatMap((j) => {
                const tileKey = j.generatedAdId ?? j.adUnitId;
                const ownReframes = reframeTiles.filter(
                  (t) => t.sourceGeneratedAdId === j.generatedAdId,
                );
                return [
                  <JobTile
                    key={tileKey}
                    job={j}
                    onRetry={() => j.generatedAdId && handleRetry(j.generatedAdId)}
                    onFreshRegen={
                      isMaster && j.generatedAdId && !j.isManual
                        ? () => handleFreshRegen(j.generatedAdId!)
                        : undefined
                    }
                    onDownload={() =>
                      j.imageUrl &&
                      downloadOne(j.imageUrl, `${j.customName?.trim() || j.adUnitName}-${j.aspectRatio.replace(":", "x")}`)
                    }
                    onOpen={() => j.imageUrl && setLightboxJob(j)}
                    onEdit={
                      canOpenPromptImageEditor({
                        mediaType: "image",
                        imageUrl: j.imageUrl,
                        adId: j.generatedAdId,
                        status: j.status === "completed" ? "ready" : j.status,
                      })
                        ? () => setEditingJob(j)
                        : undefined
                    }
                    onSave={() => j.generatedAdId && handleSave(j.generatedAdId)}
                    onRename={j.generatedAdId ? (name) => handleRename(j.generatedAdId!, name) : undefined}
                    onReframe={(ratio) => startReframe(j, ratio)}
                    onCopy={(ratio) => handleCopyAd(j, ratio)}
                    isCopying={!!j.generatedAdId && copyingAdIds.has(j.generatedAdId)}
                    retryDisabled={isRunning}
                    aspectRatio={j.aspectRatio}
                    editing={editingTileIds.has(j.generatedAdId ?? j.adUnitId)}
                    editingPhase={tileEditPhases[j.generatedAdId ?? j.adUnitId] ?? null}
                    editError={tileEditErrors[j.generatedAdId ?? j.adUnitId]}
                    showSafeZone={showSafeZone}
                    undoButton={
                      j.generatedAdId && brandId ? (
                        <>
                          <UndoAdEditButton
                            adId={j.generatedAdId}
                            brandId={brandId}
                            undoableAdIds={undoableAdIds}
                            onUndone={(url) => {
                              setOverride(j.generatedAdId!, { imageUrl: url });
                              queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
                            }}
                            className="h-7 rounded-full text-[11px] w-full"
                          />
                          <RedoAdEditButton
                            adId={j.generatedAdId}
                            brandId={brandId}
                            redoableAdIds={redoableAdIds}
                            onRedone={(url) => {
                              setOverride(j.generatedAdId!, { imageUrl: url });
                              queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, activeGenerationId] });
                            }}
                            className="h-7 rounded-full text-[11px] w-full"
                          />
                        </>
                      ) : null
                    }
                  />,
                  ...ownReframes.map((t) => (
                    <ReframeTileCard
                      key={t.id}
                      tile={t}
                      onSave={() => saveReframeTile(t)}
                      onDismiss={() => dismissReframeTile(t.id)}
                      onRetry={() => retryReframeTile(t)}
                      onEdit={() => setEditingReframeId(t.id)}
                      onDownload={() => t.imageUrl && downloadOne(t.imageUrl, `${t.sourceAdName}-${t.aspectRatio.replace(":", "x")}`)}
                      editing={editingTileIds.has(t.id)}
                      editingPhase={tileEditPhases[t.id] ?? null}
                      editError={tileEditErrors[t.id]}
                    />
                  )),
                ];
              })}
            </div>
          )}

          {/* Task #2046 follow-up — master-only enriched Recent generations
              table (up to 10 runs with thumbnails); non-masters keep the
              existing Previous Sessions panel unchanged. */}
          {recentGenerationsPanelEnabled ? (
            <AdsExpressRecentGenerations
              brandId={brandId!}
              variant="standard"
              excludeGenerationId={activeGenerationId}
              adUnits={adUnits}
              onOpenRun={(id) => {
                setActiveGenerationId(id);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          ) : (
            <AdsExpressPreviousSessions
              brandId={brandId!}
              variant="standard"
              excludeGenerationId={activeGenerationId}
              adUnits={adUnits}
            />
          )}

          {editingJob?.imageUrl && (
            <InpaintingModal
              open={!!editingJob}
              onClose={() => setEditingJob(null)}
              imageUrl={editingJob.imageUrl}
              adId={editingJob.generatedAdId ?? undefined}
              brandId={brandId}
              onEditFailed={(message) => setTileEditErrors((prev) => ({ ...prev, [editingJob.generatedAdId ?? editingJob.adUnitId]: message }))}
              onAccept={async (newUrl) => {
                const id = editingJob.adUnitId;
                const genAdId = editingJob.generatedAdId;
                if (genAdId) {
                  await persistAcceptedEdit(genAdId, newUrl);
                  setOverride(genAdId, { imageUrl: newUrl });
                }
                setLightboxJob((prev) => (prev?.adUnitId === id ? { ...prev, imageUrl: newUrl } : prev));
              }}
            />
          )}

          {(() => {
            const editingTile = editingReframeId ? reframeTiles.find((t) => t.id === editingReframeId) : null;
            if (!editingTile?.imageUrl) return null;
            return (
              <InpaintingModal
                open={!!editingTile}
                onClose={() => setEditingReframeId(null)}
                imageUrl={editingTile.imageUrl}
                adId={editingTile.sourceGeneratedAdId}
                brandId={brandId}
                onEditFailed={(message) => setTileEditErrors((prev) => ({ ...prev, [editingTile.id]: message }))}
                onAccept={(newUrl) => {
                  // Inpainted edit lives only in the tile until the user
                  // saves — we update the local imageUrl and reset to
                  // `ready` so Save will write the edited version.
                  updateReframeTile(editingTile.id, { imageUrl: newUrl, status: "ready" });
                  setEditingReframeId(null);
                }}
              />
            );
          })()}

          <Dialog open={!!lightboxJob} onOpenChange={(open) => !open && setLightboxJob(null)}>
            <DialogContent className="max-w-5xl p-0 overflow-hidden bg-background" data-testid="dialog-lightbox">
              <DialogTitle className="sr-only">{lightboxJob ? lightboxJob.customName?.trim() || lightboxJob.adUnitName : "Ad image"}</DialogTitle>
              {lightboxJob?.imageUrl && (
                <div className="flex flex-col">
                  <div className="bg-muted/30 flex items-center justify-center max-h-[80vh] overflow-auto">
                    <div className="relative">
                      <AdImage
                        src={lightboxJob.imageUrl}
                        alt={lightboxJob.customName?.trim() || lightboxJob.adUnitName}
                        className="max-w-full max-h-[80vh] object-contain"
                        data-testid="img-lightbox"
                      />
                      {showSafeZone && lightboxJob.aspectRatio === "9:16" && <SafeZoneOverlay />}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 p-3 border-t border-border/50">
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">{lightboxJob.customName?.trim() || lightboxJob.adUnitName}</div>
                      {lightboxJob.templateName && (
                        <div className="text-[11px] text-muted-foreground truncate" data-testid="text-lightbox-template">
                          Template: {lightboxJob.templateName}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {lightboxJob.aspectRatio === "9:16" && (
                        <SafeZoneToggle active={showSafeZone} onToggle={() => setShowSafeZone((v) => !v)} />
                      )}
                      {notesEnabled && lightboxJob.isSaved && lightboxJob.generatedAdId && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setNotesDialogAd({
                              adId: lightboxJob.generatedAdId!,
                              adName: lightboxJob.customName?.trim() || lightboxJob.adUnitName,
                            })
                          }
                          className="rounded-full text-[11px] h-8"
                          data-testid="button-lightbox-notes"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Notes{(noteCounts[lightboxJob.generatedAdId] ?? 0) > 0 ? ` (${noteCounts[lightboxJob.generatedAdId]})` : ""}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="rounded-full text-[11px] h-8"
                        data-testid="button-open-new-tab"
                      >
                        <a href={lightboxJob.imageUrl} target="_blank" rel="noopener noreferrer">
                          Open in new tab
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadOne(lightboxJob.imageUrl!, lightboxJob.customName?.trim() || lightboxJob.adUnitName)}
                        className="rounded-full text-[11px] h-8"
                        data-testid="button-lightbox-download"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                      {/* Task #2439 — master-only fresh-prompt regenerate from the
                          detail view (server enforces the real gate). */}
                      {isMaster && lightboxJob.generatedAdId && !lightboxJob.isManual && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const id = lightboxJob.generatedAdId!;
                            setLightboxJob(null);
                            void handleFreshRegen(id);
                          }}
                          className="rounded-full text-[11px] h-8"
                          data-testid="button-lightbox-fresh-regen"
                          title="Re-run the prompt workflow with this ad's original run inputs, then render a NEW creative (Regen reuses the stored prompt)"
                        >
                          <Wand2 className="h-3 w-3 mr-1" />
                          Fresh prompt
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {notesDialogAd && brandId && (
            <AdNotesDialog
              open={!!notesDialogAd}
              onOpenChange={(o) => { if (!o) setNotesDialogAd(null); }}
              adId={notesDialogAd.adId}
              brandId={brandId}
              adName={notesDialogAd.adName}
            />
          )}
        </>
      )}

      <Dialog open={newProjectOpen} onOpenChange={(o) => { if (!o) { setNewProjectOpen(false); setNewProjectName(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-new-project">
          <DialogTitle>New project</DialogTitle>
          <p className="text-xs text-muted-foreground -mt-2">
            Group related runs together. You can move ads between projects later.
          </p>
          <div className="space-y-3 pt-2">
            <Input
              autoFocus
              placeholder="e.g. Spring Campaign 2026"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newProjectName.trim() && !creatingProject) {
                  e.preventDefault();
                  (document.querySelector('[data-testid="button-create-project"]') as HTMLButtonElement | null)?.click();
                }
              }}
              disabled={creatingProject}
              data-testid="input-new-project-name"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setNewProjectOpen(false); setNewProjectName(""); }}
                disabled={creatingProject}
                data-testid="button-cancel-new-project"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const name = newProjectName.trim();
                  if (!name || !brandId) return;
                  setCreatingProject(true);
                  try {
                    const res = await apiRequest("POST", `/api/brands/${brandId}/projects`, { name });
                    const created = (await res.json()) as { id: string; name: string };
                    if (!created?.id) {
                      throw new Error("Server did not return a project id");
                    }
                    // Optimistically add to cache so the picker reflects the
                    // new project on the very next render, then kick off a
                    // background refetch to reconcile. Dedupe in case a
                    // concurrent refetch already inserted it.
                    queryClient.setQueryData<Project[]>(
                      [`/api/brands/${brandId}/projects`],
                      (prev) => {
                        const next = created as Project;
                        if (!prev) return [next];
                        return prev.some((p) => p.id === next.id) ? prev : [next, ...prev];
                      },
                    );
                    queryClient.invalidateQueries({
                      queryKey: [`/api/brands/${brandId}/projects`],
                    });
                    setProjectId(created.id);
                    setNewProjectOpen(false);
                    setNewProjectName("");
                    toast({ title: "Project created", description: created.name });
                  } catch (e: unknown) {
                    const msg = e instanceof Error ? e.message : String(e);
                    console.error("[ads-express] create project failed", e);
                    toast({ title: "Couldn't create project", description: msg, variant: "destructive" });
                  } finally {
                    setCreatingProject(false);
                  }
                }}
                disabled={creatingProject || !newProjectName.trim()}
                data-testid="button-create-project"
              >
                {creatingProject && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reframe is rendered inline as new tiles in the results grid
          (see ReframeTileCard) — no modal needed. */}

      {isAdmin && (
        <Dialog open={categoriesManagerOpen} onOpenChange={setCategoriesManagerOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="dialog-manage-categories">
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5 text-primary" />
              Manage Template Categories
            </DialogTitle>
            <TemplateCategoriesManager />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ProgressStep({ label, filled }: { label: string; filled: boolean }) {
  return (
    <div
      className={[
        "flex items-center gap-1.5 rounded-full pl-1 pr-2.5 py-0.5 transition-colors",
        filled ? "text-foreground" : "text-muted-foreground/70",
      ].join(" ")}
    >
      <span
        className={[
          "h-4 w-4 rounded-full flex items-center justify-center transition-colors ring-1",
          filled
            ? "bg-primary text-primary-foreground ring-primary/40"
            : "bg-background text-muted-foreground/50 ring-border",
        ].join(" ")}
      >
        {filled ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <span className="h-1 w-1 rounded-full bg-current" />}
      </span>
      <span className="text-[11px] font-medium tracking-wide">{label}</span>
    </div>
  );
}

function ProgressConnector({ filled }: { filled: boolean }) {
  return (
    <span
      aria-hidden
      className={[
        "h-px w-3 transition-colors",
        filled ? "bg-primary/60" : "bg-border",
      ].join(" ")}
    />
  );
}


const ASPECT_CLASS: Record<AspectRatio, string> = {
  "1:1": "aspect-square",
  "4:5": "aspect-[4/5]",
  "9:16": "aspect-[9/16]",
  "16:9": "aspect-[16/9]",
  "2:3": "aspect-[2/3]",
};

function JobTile({
  job, onRetry, onFreshRegen, onDownload, onOpen, onEdit, onSave, onRename, onReframe, onCopy, isCopying, retryDisabled, aspectRatio, editing, editingPhase, editError, showSafeZone, undoButton,
}: {
  job: Job;
  onRetry: () => void;
  // Task #2439 — master-only fresh-prompt regenerate (re-runs the Sim
  // prompt workflow, new creative). Absent for non-masters/manual tiles.
  onFreshRegen?: () => void;
  onDownload: () => void;
  onOpen: () => void;
  onEdit?: () => void;
  onSave: () => void;
  onRename?: (name: string | null) => void;
  onReframe: (ratio: "1:1" | "4:5" | "9:16") => void;
  // Task #2161 — duplicate this SAVED tile into My Ads. ratio=null →
  // "Copy as is" (exact duplicate, own storage); a ratio → "Copy &
  // reframe" (new saved ad at that ratio, original untouched).
  onCopy: (ratio: AspectRatio | null) => void;
  isCopying?: boolean;
  retryDisabled: boolean;
  aspectRatio: AspectRatio;
  editing?: boolean;
  editingPhase?: string | null;
  editError?: string;
  showSafeZone?: boolean;
  // Task #2040 — "Undo edit" affordance for saved tiles with a restorable
  // previous version (null when unavailable — the row simply doesn't render).
  undoButton?: React.ReactNode;
}) {
  const statusLabel = {
    queued: "Queued — starting shortly",
    rendering: "Generating...",
    completed: "Done",
    failed: "Failed",
    cancelled: "Cancelled",
  }[job.status];

  const statusColor = {
    queued: "text-muted-foreground",
    rendering: "text-violet-600 dark:text-violet-400",
    completed: "text-emerald-600 dark:text-emerald-400",
    failed: "text-destructive",
    cancelled: "text-muted-foreground",
  }[job.status];

  const isWorking = job.status === "rendering";
  // "Regenerating" = a previously-completed tile is being re-rendered in
  // place. We detect it as ANY non-terminal working status (rendering OR
  // queued — the worker may transition through `queued` while waiting for
  // a Fal slot) that still carries the prior image URL. In this state we
  // keep the old image visible (with a spinner overlay) and keep the
  // action area mounted (disabled) so the tile stays the exact same size
  // and doesn't move within the CSS grid throughout the full regen
  // lifecycle.
  const isRegenerating =
    (((job.status === "rendering" || job.status === "queued") && !!job.imageUrl) ||
      (!!editing && !!job.imageUrl));
  const showCompletedShell = job.status === "completed" || isRegenerating;

  return (
    <Card className="overflow-hidden" data-testid={`tile-job-${job.adUnitId}`}>
      <div className={`${ASPECT_CLASS[aspectRatio]} bg-muted/30 relative flex items-center justify-center`}>
        {showCompletedShell && job.imageUrl ? (
          <button
            type="button"
            onClick={onOpen}
            disabled={isRegenerating}
            className="group block w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default"
            aria-label={`Open ${job.customName?.trim() || job.adUnitName} full size`}
            data-testid={`button-open-${job.adUnitId}`}
          >
            <AdImage
              src={job.imageUrl}
              thumbWidth={640}
              alt={job.customName?.trim() || job.adUnitName}
              className={`w-full h-full object-cover transition-transform ${isRegenerating ? "opacity-60" : "group-hover:scale-[1.02]"}`}
              data-testid={`img-job-${job.adUnitId}`}
            />
            {!isRegenerating && (
              <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="h-3 w-3" />
              </span>
            )}
            {isRegenerating && (
              <span
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/60 backdrop-blur-[1px]"
                data-testid={`regenerating-${job.adUnitId}`}
              >
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <span className="text-[11px] font-medium text-foreground">
                  {editing ? imageEditPhaseLabel(editingPhase) : "Regenerating…"}
                </span>
              </span>
            )}
            {showSafeZone && aspectRatio === "9:16" && !isRegenerating && <SafeZoneOverlay />}
          </button>
        ) : isWorking ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-[11px]">{statusLabel}</span>
          </div>
        ) : job.status === "failed" ? (
          <div className="flex flex-col items-center gap-2 px-3 text-center">
            <XCircle className="h-6 w-6 text-destructive" />
            <span className="text-[11px] text-destructive line-clamp-3" data-testid={`text-error-${job.adUnitId}`}>
              {job.error || "Failed"}
            </span>
          </div>
        ) : (
          <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-xs font-semibold tracking-tight flex-1 min-w-0" data-testid={`text-job-name-${job.adUnitId}`}>
            {onRename && job.status === "completed" ? (
              <InlineRenameTitle
                customName={job.customName}
                fallbackName={job.adUnitName}
                onSave={onRename}
                className="[&>span:first-child]:line-clamp-2"
                inputClassName="text-xs font-semibold"
                testId={`job-name-${job.adUnitId}`}
              />
            ) : (
              <span className="line-clamp-2">{job.customName?.trim() || job.adUnitName}</span>
            )}
            {job.isManual && (
              <span
                className="mt-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                data-testid={`badge-manual-${job.adUnitId}`}
              >
                Manual upload
              </span>
            )}
            {!job.isManual && job.templateName && job.templateName !== (job.customName?.trim() || job.adUnitName) && (
              <span
                className="mt-1 block text-[10px] font-normal text-muted-foreground truncate"
                title={`Template: ${job.templateName}`}
                data-testid={`text-template-${job.adUnitId}`}
              >
                Template: {job.templateName}
              </span>
            )}
            {job.isManual && (job.manualTags?.length ?? 0) > 0 && (
              <span className="mt-1 flex flex-wrap gap-1">
                {job.manualTags!.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground max-w-full truncate"
                    data-testid={`tag-manual-${job.adUnitId}`}
                  >
                    {t}
                  </span>
                ))}
              </span>
            )}
          </h3>
          <span className={`text-[10px] font-medium shrink-0 inline-flex items-center gap-1 ${statusColor}`} data-testid={`text-status-${job.adUnitId}`}>
            {job.status === "completed" && <CheckCircle2 className="h-3.5 w-3.5" />}
            <span className={job.status === "completed" ? "sr-only sm:not-sr-only" : ""}>{statusLabel}</span>
          </span>
        </div>
        {editError && (
          <div className="text-xs text-destructive px-3 py-2" role="alert" data-testid="text-image-edit-failure">
            {editError}
          </div>
        )}
        {(job.status === "failed" || job.status === "cancelled") && !job.isManual && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              disabled={retryDisabled}
              className="w-full h-7 rounded-full text-[11px]"
              data-testid={`button-retry-${job.adUnitId}`}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
            Retry
            </Button>
            {onFreshRegen && (
              <Button
                variant="outline"
                size="sm"
                onClick={onFreshRegen}
                disabled={retryDisabled}
                className="w-full h-7 rounded-full text-[11px]"
                data-testid={`button-fresh-regen-${job.adUnitId}`}
                title="Re-run the prompt workflow with this ad's original run inputs, then render a new creative (Regen/Retry reuses the stored prompt)"
              >
                <Wand2 className="h-3 w-3 mr-1" />
                Fresh prompt
              </Button>
            )}
          </>
        )}
        {showCompletedShell && (
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                disabled={isRegenerating}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-edit-${job.adUnitId}`}
              >
                <Paintbrush className="h-3 w-3 mr-1" />
                Edit
              </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={onDownload}
                disabled={isRegenerating}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-download-${job.adUnitId}`}
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
            {/* Task #1705 — manual uploads have no template/prompt behind
                them, so the template-only affordances (Regen / Reframe) are
                hidden; Save takes the full row. */}
            <div className={job.isManual ? "grid grid-cols-1 gap-1.5" : "grid grid-cols-3 gap-1.5"}>
              {!job.isManual && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                disabled={isRegenerating}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-regen-${job.adUnitId}`}
                title="Generate this ad again from scratch"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Regen
              </Button>
              )}
              {!job.isManual && onFreshRegen && (
              <Button
                variant="outline"
                size="sm"
                onClick={onFreshRegen}
                disabled={isRegenerating}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-fresh-regen-${job.adUnitId}`}
                title="Re-run the prompt workflow with this ad's original run inputs, then render a NEW creative (Regen reuses the stored prompt — same creative, new roll)"
              >
                <Wand2 className="h-3 w-3 mr-1" />
                Fresh prompt
              </Button>
              )}
              {!job.isManual && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!job.generatedAdId || isRegenerating}
                    className="h-7 rounded-full text-[11px]"
                    data-testid={`button-reframe-${job.adUnitId}`}
                    title={
                      !job.generatedAdId
                        ? "Reframe unavailable for this run"
                        : "Re-render this ad at a different aspect ratio"
                    }
                  >
                    <Crop className="h-3 w-3 mr-1" />
                    Reframe
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-32">
                  {(["1:1", "4:5", "9:16"] as const).map((r) => (
                    <DropdownMenuItem
                      key={r}
                      onSelect={() => onReframe(r)}
                      disabled={r === aspectRatio}
                      data-testid={`menu-reframe-${job.adUnitId}-${r.replace(":", "x")}`}
                    >
                      {r}
                      {r === aspectRatio && (
                        <span className="ml-auto text-[10px] text-muted-foreground">current</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
              <Button
                variant={job.isSaved ? "default" : "outline"}
                size="sm"
                onClick={onSave}
                disabled={!job.generatedAdId || job.isSaved || job.saving || isRegenerating}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-save-${job.adUnitId}`}
                title={
                  !job.generatedAdId
                    ? "Save unavailable for this run"
                    : job.isSaved
                    ? "Saved to library"
                    : "Save to library"
                }
              >
                {job.saving ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : job.isSaved ? (
                  <BookmarkCheck className="h-3 w-3 mr-1" />
                ) : (
                  <Bookmark className="h-3 w-3 mr-1" />
                )}
                {job.isSaved ? "Saved" : job.saving ? "Saving…" : "Save"}
              </Button>
            </div>
            {/* Task #2161 — saved image tiles get a copy menu: an exact
                duplicate or a duplicate reframed to a new ratio. Both land
                in My Ads; the original tile is never touched. */}
            {!job.isManual && job.isSaved && job.generatedAdId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isCopying || isRegenerating}
                    className="w-full h-7 rounded-full text-[11px]"
                    data-testid={`button-copy-${job.adUnitId}`}
                    title="Duplicate this saved ad into My Ads"
                  >
                    {isCopying ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Copying…
                      </>
                    ) : (
                      <>
                        <CopyPlus className="h-3 w-3 mr-1" />
                        Copy…
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem
                    onSelect={() => onCopy(null)}
                    data-testid={`menu-copy-as-is-${job.adUnitId}`}
                  >
                    Copy as is
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {(["1:1", "4:5", "9:16"] as const)
                    .filter((r) => r !== aspectRatio)
                    .map((r) => (
                      <DropdownMenuItem
                        key={r}
                        onSelect={() => onCopy(r)}
                        data-testid={`menu-copy-reframe-${job.adUnitId}-${r.replace(":", "x")}`}
                      >
                        Copy &amp; reframe to {r}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {undoButton}
          </div>
        )}
      </div>
    </Card>
  );
}

function ReframeTileCard({
  tile, onSave, onDismiss, onRetry, onEdit, onDownload, editing, editingPhase, editError,
}: {
  tile: {
    id: string;
    sourceAdUnitId: string;
    sourceAdName: string;
    aspectRatio: "1:1" | "4:5" | "9:16";
    status: "loading" | "ready" | "error" | "saving" | "saved";
    imageUrl?: string;
    errorMsg?: string;
  };
  onSave: () => void;
  onDismiss: () => void;
  onRetry: () => void;
  onEdit: () => void;
  onDownload: () => void;
  editing?: boolean;
  editingPhase?: string | null;
  editError?: string;
}) {
  const isWorking = tile.status === "loading";
  const statusLabel =
    tile.status === "loading" ? "Generating..." :
    tile.status === "saving" ? "Saving..." :
    tile.status === "saved" ? "Saved" :
    tile.status === "error" ? "Failed" :
    "Ready";
  const statusColor =
    tile.status === "loading" || tile.status === "saving" ? "text-violet-600 dark:text-violet-400" :
    tile.status === "saved" ? "text-emerald-600 dark:text-emerald-400" :
    tile.status === "error" ? "text-destructive" :
    "text-muted-foreground";

  return (
    <Card
      className="overflow-hidden border-violet-300/60 dark:border-violet-700/40 ring-1 ring-violet-300/40 dark:ring-violet-700/30"
      data-testid={`tile-reframe-${tile.id}`}
    >
      <div className={`${ASPECT_CLASS[tile.aspectRatio]} bg-muted/30 relative flex items-center justify-center`}>
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-violet-600/90 text-white text-[10px] font-semibold px-2 py-0.5 shadow-sm z-10">
          <Crop className="h-3 w-3" />
          Reframe {tile.aspectRatio}
        </span>
        {editing && tile.imageUrl && (
          <span
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-background/60 backdrop-blur-[1px]"
            data-testid={`reframe-regenerating-${tile.id}`}
          >
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-[11px] font-medium text-foreground">{imageEditPhaseLabel(editingPhase)}</span>
          </span>
        )}
        {tile.imageUrl ? (
          tile.imageUrl ? (
            <AdImage
              src={tile.imageUrl}
              alt={`Reframed ${tile.sourceAdName} to ${tile.aspectRatio}`}
              className="w-full h-full object-contain"
              data-testid={`img-reframe-${tile.id}`}
            />
          ) : (
            <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
          )
        ) : isWorking ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-[11px]">Generating...</span>
            <span className="text-[10px] text-muted-foreground/70">10–30 seconds</span>
          </div>
        ) : tile.status === "error" ? (
          <div className="flex flex-col items-center gap-2 px-3 text-center">
            <XCircle className="h-6 w-6 text-destructive" />
            <span className="text-[11px] text-destructive line-clamp-3" data-testid={`text-reframe-error-${tile.id}`}>
              {tile.errorMsg || "Reframe failed"}
            </span>
          </div>
        ) : (
          <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
        )}
      </div>
      <div className="p-3 space-y-2">
        {tile.status === "error" && tile.imageUrl && (
          <p className="text-xs text-destructive">{tile.errorMsg}</p>
        )}
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-xs font-semibold tracking-tight line-clamp-2 flex-1" data-testid={`text-reframe-name-${tile.id}`}>
            {tile.sourceAdName}
          </h3>
          <span className={`text-[10px] font-medium shrink-0 inline-flex items-center gap-1 ${statusColor}`} data-testid={`text-reframe-status-${tile.id}`}>
            {tile.status === "saved" && <CheckCircle2 className="h-3.5 w-3.5" />}
            {statusLabel}
          </span>
        </div>
        {editError && <div className="text-xs text-destructive" role="alert" data-testid="text-image-edit-failure">{editError}</div>}
        {tile.status === "error" && (
          <div className="grid grid-cols-2 gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="h-7 rounded-full text-[11px]"
              data-testid={`button-reframe-retry-${tile.id}`}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Retry
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDismiss}
              className="h-7 rounded-full text-[11px]"
              data-testid={`button-reframe-dismiss-${tile.id}`}
            >
              <X className="h-3 w-3 mr-1" />
              Dismiss
            </Button>
          </div>
        )}
        {(tile.status === "ready" || tile.status === "saving" || tile.status === "saved") && (
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                disabled={!tile.imageUrl || tile.status === "saving"}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-reframe-edit-${tile.id}`}
              >
                <Paintbrush className="h-3 w-3 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDownload}
                disabled={!tile.imageUrl}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-reframe-download-${tile.id}`}
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-1.5">
              <Button
                variant={tile.status === "saved" ? "default" : "outline"}
                size="sm"
                onClick={onSave}
                disabled={tile.status !== "ready"}
                className="h-7 rounded-full text-[11px]"
                data-testid={`button-reframe-save-${tile.id}`}
                title={tile.status === "saved" ? "Saved to My Ads" : "Save to My Ads"}
              >
                {tile.status === "saving" ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : tile.status === "saved" ? (
                  <BookmarkCheck className="h-3 w-3 mr-1" />
                ) : (
                  <Bookmark className="h-3 w-3 mr-1" />
                )}
                {tile.status === "saved" ? "Saved" : tile.status === "saving" ? "Saving" : "Save"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDismiss}
                disabled={tile.status === "saving"}
                className="h-7 w-7 p-0 rounded-full"
                data-testid={`button-reframe-close-${tile.id}`}
                title="Remove from grid"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function TemplateCard({
  unit, checked, disabled, ineligible = false, brandId, onToggle,
}: {
  unit: AdUnitType;
  checked: boolean;
  disabled: boolean;
  ineligible?: boolean;
  brandId: string;
  onToggle: () => void;
}) {
  const imageRecords = toImageRecordsAE(unit.exampleImages);
  const thumb = displayUrl(imageRecords[0] ?? null);
  const shortName = shortNameOf(unit.slug, unit.name);
  const showOriginal = shortName !== unit.name;
  // Ineligible cards stay interactive only enough to surface their tooltip,
  // but clicking does nothing (toggleTemplate also guards against this).
  const isDisabled = disabled || ineligible;

  return (
    <div className="flex flex-col">
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={ineligible ? undefined : onToggle}
              disabled={disabled}
              aria-pressed={checked}
              aria-disabled={ineligible}
              className={[
                "group relative flex flex-col rounded-lg overflow-hidden border-2 text-left transition-all",
                checked
                  ? "border-primary ring-2 ring-primary/30 shadow-sm"
                  : "border-border/50 hover:border-border hover:shadow-sm",
                isDisabled ? "opacity-60" : "cursor-pointer",
                disabled ? "pointer-events-none" : "",
                ineligible ? "cursor-not-allowed" : "",
              ].join(" ")}
              data-testid={`card-template-${unit.id}`}
              data-ineligible={ineligible ? "true" : undefined}
            >
              <div className="aspect-[9/16] bg-muted/30 relative overflow-hidden">
                {thumb ? (
                  <AdImage
                    src={thumb}
                    alt={shortName}
                    className={[
                      "w-full h-full object-contain transition-transform",
                      ineligible ? "grayscale" : "group-hover:scale-[1.03]",
                    ].join(" ")}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Megaphone className="h-6 w-6 text-muted-foreground/30" />
                  </div>
                )}
                {ineligible && (
                  <div
                    className="absolute top-2 left-2 h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center shadow"
                    data-testid={`indicator-ineligible-${unit.id}`}
                  >
                    <ImageOff className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </div>
                )}
                {checked && !ineligible && (
                  <div
                    className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow"
                    data-testid={`indicator-checked-${unit.id}`}
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </div>
                )}
                {checked && !ineligible && (
                  <div className="absolute inset-0 bg-primary/5 pointer-events-none" />
                )}
              </div>
              <div className="px-2.5 py-2 bg-card">
                <div
                  className="text-[12px] font-semibold leading-tight tracking-tight line-clamp-2"
                  data-testid={`text-template-short-${unit.id}`}
                >
                  {shortName}
                </div>
                {ineligible && (
                  <div
                    className="mt-1 text-[10px] font-medium leading-tight text-amber-600 dark:text-amber-500"
                    data-testid={`text-ineligible-${unit.id}`}
                  >
                    No example image
                  </div>
                )}
              </div>
            </button>
          </TooltipTrigger>
          {(showOriginal || ineligible) && (
            <TooltipContent side="bottom" className="text-xs max-w-xs">
              {ineligible
                ? "This template has no example image, which is required to generate it. Add one to make it selectable."
                : unit.name}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      {/* Admin-only fix path: ineligible cards only render for admins (regular
          users have them filtered out upstream), so this link gives the one
          role that CAN add an example image a direct jump to the editor. */}
      {ineligible && brandId && (
        <Link
          href={`/brands/${brandId}/ad-units/${unit.id}`}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
          data-testid={`link-add-example-${unit.id}`}
        >
          <ImagePlus className="h-3 w-3" />
          Add example image
        </Link>
      )}
    </div>
  );
}

function EmptyResource({ brandId, resource }: { brandId: string; resource: string }) {
  const map: Record<string, { title: string; desc: string; href: string; cta: string; icon: React.ReactNode }> = {
    personas: {
      title: "No personas yet",
      desc: "Add at least one persona for this brand to use Ads Express.",
      href: `/brands/${brandId}/personas`,
      cta: "Go to Personas",
      icon: <Users className="h-6 w-6 text-primary" />,
    },
    products: {
      title: "No products yet",
      desc: "Add at least one product for this brand to use Ads Express.",
      href: `/brands/${brandId}/products`,
      cta: "Go to Products",
      icon: <Package className="h-6 w-6 text-primary" />,
    },
    "products-or-offers": {
      title: "Nothing to promote yet",
      desc: "Add at least one product or offer for this brand to use Ads Express.",
      href: `/brands/${brandId}/products`,
      cta: "Go to Products",
      icon: <Package className="h-6 w-6 text-primary" />,
    },
    angles: {
      title: "No narrative angles yet",
      desc: "Generate narrative angles in the Narrative Engine before running Ads Express.",
      href: `/brands/${brandId}/narratives`,
      cta: "Go to Narrative Engine",
      icon: <Sparkles className="h-6 w-6 text-primary" />,
    },
    "ad-units": {
      title: "No static ad templates yet",
      desc: "Create at least one static ad unit template to use Ads Express.",
      href: `/brands/${brandId}/ad-units`,
      cta: "Go to Static Ad Units",
      icon: <Megaphone className="h-6 w-6 text-primary" />,
    },
  };
  const info = map[resource];
  return (
    <Card className="border-dashed border-border/50">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
          {info.icon}
        </div>
        <h3 className="font-semibold tracking-tight mb-1">{info.title}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">{info.desc}</p>
        <Link href={info.href}>
          <Button variant="outline" className="rounded-full" data-testid={`button-empty-${resource}`}>
            {info.cta}
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}
